import { useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import DOMPurify from "dompurify";
import { formatAmount } from "@/utils/formatAmount";
import { displayUnit } from "@/utils/displayUnit";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, FileText, Upload, Paperclip, ChevronDown, ChevronUp, CheckCircle2, XCircle, Eye, Pencil, Settings2, Trash2, Phone, Mail, RotateCcw, ExternalLink, Building2, User as UserIcon, Bell, BellOff } from "lucide-react";
import { useOrder, usePenaltyCalc, useTerminateOrder, useCEOUpdateOrder, useForceAcceptOrder, useRestoreOrder, useDeleteOrder, useCloseOrder, useAnnulOrder, useReopenOrder, useOrderSubscription, useSetLineItemResultFiles } from "../hooks/useOrders";
import { useOrderResultFiles } from "../hooks/useResultFiles";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { Modal } from "@/components/ui/Modal";
import { useAuthStore } from "@/store/auth";
import apiClient from "@/api/client";
import { FINISHED_ORDER_STATUSES, ORDER_STATUS_LABELS, PRIORITY_LABELS, WEEKLY_REPORT_STATUS_LABELS, type OrderLineItem, type OrderStatus, type PenaltyCalcTerm, type PockRequest, type WeeklyReportStatus } from "../types";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { DatePicker } from "@/components/ui/DatePicker";
import { RichTextEditor } from "@/components/ui/RichTextEditor";
import { OrderChangePanel } from "@/features/orders/components/OrderChangePanel";
import { OrderCommentsSection } from "@/features/orders/components/OrderCommentsSection";
import { OrderTypeBadge } from "@/features/orders/components/OrderTypeBadge";
import { ExternalCostsSection } from "@/features/orders/components/ExternalCostsSection";
import { OrderAttachmentsSection } from "@/features/orders/components/OrderAttachmentsSection";
import { OrderOwnersListSection } from "@/features/orders/components/OrderOwnersListSection";
import { OrderProjectFilesSection } from "@/features/orders/components/OrderProjectFilesSection";
import { OrderResultFilesSection } from "@/features/orders/components/OrderResultFilesSection";
import { OrderMailLogSection } from "@/features/orders/components/OrderMailLogSection";
import { OrderEventsSection } from "@/features/orders/components/OrderEventsSection";
import { OrderMilestonesSection } from "@/features/orders/components/OrderMilestonesSection";
import { ContractorViewModal } from "@/features/orders/components/ContractorViewModal";
import { OrderAccessGrantsSection } from "@/features/orders/components/OrderAccessGrantsSection";
import { OrderGrantedStaffCard } from "@/features/orders/components/OrderGrantedStaffCard";
import { ContractorOrderDetailPage } from "./ContractorOrderDetailPage";
import { ContractorPreviewProvider } from "../contractorPreview";
import { MinOrderAmountNotice } from "../components/MinOrderAmountNotice";
import { AcceptanceDeadlineNotice } from "../components/AcceptanceDeadlineNotice";
import { OrderAccessProvider, isExecutionAccess, orderAccessState, useOrderAccess } from "../orderAccess";
import { OrderRatingSection } from "@/features/rfq/components/OrderRatingSection";
import { canManageContractors } from "@/features/contractors/utils";
// DLC_01 bridge: WPW handover preview (działka | WPW) — read-only here, self-hides for non-WPW.
import { WpwHandoverPanel } from "@/features/power_projects/components/WpwHandoverPanel";
import { DesignStageOrderPanel } from "@/features/power_projects/components/DesignStageOrderPanel";
import { MdcpPointsPanel } from "@/features/power_projects/components/MdcpPointsPanel";
import { WodyOrderCrossingsPanel } from "@/features/power_projects/components/WodyOrderCrossingsPanel";
import { useApprovePockRequest, useRejectPockRequest } from "../hooks/usePockRequests";
import { useWeeklyReports, useAcceptWeeklyReport, useRejectWeeklyReport, useMarkWeeklyReportRead, useUpdateOrderReportSettings } from "../hooks/useWeeklyReports";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useConversationDeepLink } from "@/hooks/useConversationDeepLink";
import { acceptedQtyFromPct, completionPctFromQty, isAmountOverridden, qtyInputValue } from "@/features/deliveries/utils/pockMath";
import { isSettledProtocol, protocolNetValue } from "@/features/deliveries/utils/protocolValue";
import { apiErrorText } from "@/features/deliveries/utils/apiErrorText";
import { DocumentAttachmentsPanel } from "@/features/deliveries/components/DocumentAttachmentsPanel";
import type { DocumentAttachment, SourceAction } from "@/features/deliveries/types";
import { formatDate } from "@/utils/formatDate";


const statusColors: Record<OrderStatus, "gray" | "blue" | "green" | "red" | "yellow" | "purple" | "orange"> = {
  sent: "blue", accepted: "green", rejected: "red", in_progress: "orange",
  in_progress_partial: "yellow", submitted_for_acceptance: "purple",
  accepted_final: "green", closed: "gray", cancelled: "gray",
};

interface ProtocolItem {
  id: string;
  order_line_item: string;
  completion_percentage: string;
  quality_pct?: string;
  order_line_item_quantity: string;
  order_line_item_unit_rate: string | null;
  amount_net: string | null;
  attachment_managed_file?: string | null;
  attachment_versions?: Array<{ id: string; is_superseded: boolean }>;
}

interface Protocol {
  id: string;
  protocol_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  accepted_by_contractor: boolean | null;
  pock_approved_settlement_pct?: string;
  pock_request?: string | null;
  /** POCK bez kwot — przekazanie prac przez pracownika z dostępem „Realizacja”; nie liczy się do rozliczenia. */
  without_amounts?: boolean;
  awaiting_orderer_acceptance?: boolean;
  created_at: string;
  created_by?: string;
  created_by_name: string;
  items: ProtocolItem[];
  /** Files of the whole protocol — copied along with a correction. */
  document_attachments?: DocumentAttachment[];
  /** Created from this POCK („Utwórz na podstawie istniejącego POCK"). */
  source_protocol_number?: string | null;
  /** Technical director / CEO may delete it (rejected, no settlement). */
  can_delete?: boolean;
  delete_block_reason?: string | null;
}

interface CreatedProtocol {
  id: string;
  items: Array<{
    id: string;
    order_line_item: string;
    order_line_item_name: string;
    order_line_item_unit: string;
    order_line_item_quantity: string;
    completion_percentage: string;
    attachment: string | null;
    /** Set when the line's file was copied from the base protocol. */
    attachment_managed_file?: string | null;
  }>;
}

// Per-item form state
// completion_percentage is derived: (qty / ordered_qty) * quality_pct
interface ItemForm {
  order_line_item: string;
  qty: string; // accepted qty (user input)
  quality_pct: string; // quality % (0-100, default 100, independent of qty)
  amount_net: string; // manual override; empty = auto-calculated
  is_amount_manual: boolean;
  attachment_required: boolean;
}

const fmtQty = (val: string | number) => String(Math.round(parseFloat(String(val)) || 0));

const ACTIVE_STATUSES: OrderStatus[] = [
  "sent", "accepted", "in_progress", "in_progress_partial", "submitted_for_acceptance",
];

/**
 * Karta zamówienia (GK). Wariant zależy od dostępu ZALOGOWANEJ osoby (backend:
 * apps.orders.access, pola `access_level`/`access_via_grant` zamówienia):
 * - grant „Realizacja” → ta sama strona, którą widzi podwykonawca (bez kwot, bez
 *   wniosków o zmianę i POCK), w trybie `isExecution`;
 * - grant „Podgląd” → karta GK tylko do odczytu (kontekst `readOnly`);
 * - reszta (role, zamawiający, grant „Zarządzanie”) → pełna karta GK, a `canManage`
 *   odblokowuje akcje jak u zamawiającego także osobom spoza ról.
 */
export function OrderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: order, isLoading } = useOrder(id!);
  if (!isLoading && order && isExecutionAccess(order)) {
    return (
      <ContractorPreviewProvider value={{ isPreview: false, canSeeRates: false, isExecution: true }}>
        <ContractorOrderDetailPage />
      </ContractorPreviewProvider>
    );
  }
  return (
    <OrderAccessProvider value={orderAccessState(order)}>
      <OrderDetailPageInner />
    </OrderAccessProvider>
  );
}

function OrderDetailPageInner() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const isDirector = user?.role === "director";
  const isContractorUser = user?.role === "contractor";
  // Dostęp z nadanego grantu (patrz OrderDetailPage): „Podgląd” = bez akcji,
  // „Zarządzanie” = akcje jak u zamawiającego mimo roli pracownika.
  const access = useOrderAccess();
  const subscriptionMutation = useOrderSubscription(id || "");
  // Przejście „zamówienie → projekt ZE". Bramkowane tak samo jak trasa /power-projects
  // (DLCModuleGuard): bez modułu DLC_01 link i tak odbiłby użytkownika na stronę główną.
  const canOpenZeProject = Boolean(
    user && user.role !== "contractor" && user.accessible_dlc_modules?.includes("dlc_01"),
  );
  // Przejście „zamówienie → kartoteka podwykonawcy". Bramkowane tak samo jak trasa
  // /contractors (ContractorsAccessGuard): bez tego prawa link odbiłby użytkownika
  // na stronę główną, a podwykonawca nie ogląda cudzych kartotek w ogóle.
  const canOpenContractor = Boolean(
    user && (user.role === "director" || user.role === "coordinator" || canManageContractors(user)),
  );

  const { data: order, isLoading } = useOrder(id!);
  usePageTitle(order && `Zamówienie ${order.order_number}`);
  // Powiadomienie o komentarzu prowadzi do sekcji komentarzy, nie na górę zamówienia.
  const { ref: commentsRef } = useConversationDeepLink<HTMLDivElement>(!isLoading && !!order);
  const canManagePock = (user?.role === "director" || (user?.role === "coordinator" && order?.has_finance_access === true)) && order?.status !== "rejected";
  // Approving/rejecting contractor POCK requests: also the coordinator assigned to the
  // order's project, without finance access — mirrors the backend permission
  // (IsProjectCoordinatorOrDirector on the approve/reject actions).
  // …and anyone the backend lets manage the order (assigned coordinator, orderer,
  // „Zarządzanie” grantee) — CanManageOrderObject on the approve/reject actions.
  const canReviewPockRequests = canManagePock
    || (user?.role === "coordinator" && order?.is_assigned_coordinator === true && order?.status !== "rejected")
    || (access.canManage && !access.readOnly && order?.status !== "rejected");
  const canSeePockRequests = canReviewPockRequests
    || (user?.role === "coordinator" && order?.is_assigned_coordinator === true)
    || access.canManage;
  const isActive = order ? ACTIVE_STATUSES.includes(order.status) : false;
  const { data: penaltyCalc } = usePenaltyCalc(id!, isDirector && isActive && !!order?.deadline);
  const terminateMutation = useTerminateOrder();
  const approvePockRequest = useApprovePockRequest();
  const rejectPockRequest = useRejectPockRequest();
  const [pockReviewId, setPockReviewId] = useState<string | null>(null);
  // Per request, and kept when the reviewer cancels or switches approve/reject —
  // the comment is part of the decision, not something a cancel should throw away.
  const [pockReviewComments, setPockReviewComments] = useState<Record<string, string>>({});
  const [pockReviewAction, setPockReviewAction] = useState<"approve" | "reject" | null>(null);
  const [pockApprovedPct, setPockApprovedPct] = useState("100");
  const [pockApprovedFinal, setPockApprovedFinal] = useState(false);
  const [expandedPockReqs, setExpandedPockReqs] = useState<Set<string>>(new Set());
  const startPockReview = (req: PockRequest, action: "approve" | "reject") => {
    setPockReviewId(req.id);
    setPockReviewAction(action);
    if (action === "approve") {
      setPockApprovedPct(String(Math.round(parseFloat(req.proposed_settlement_pct))));
      setPockApprovedFinal(req.proposed_is_final);
    }
    setExpandedPockReqs((p) => new Set(p).add(req.id));
  };
  const cancelPockReview = () => {
    setPockReviewId(null);
    setPockReviewAction(null);
  };

  // Weekly reports
  const navigate = useNavigate();
  const { data: weeklyReports = [] } = useWeeklyReports(id);
  const acceptWeeklyReport = useAcceptWeeklyReport();
  const rejectWeeklyReport = useRejectWeeklyReport();
  const markWeeklyReportRead = useMarkWeeklyReportRead();
  const updateReportSettings = useUpdateOrderReportSettings(id!);
  const [weeklyRejectId, setWeeklyRejectId] = useState<string | null>(null);
  const [weeklyRejectComment, setWeeklyRejectComment] = useState("");
  const [showReportSettingsEdit, setShowReportSettingsEdit] = useState(false);
  const [reportSettingsForm, setReportSettingsForm] = useState({
    requires_weekly_reports: null as boolean | null,
    report_day_of_week: "" as string,
    report_due_time: "" as string,
  });

  // Penalty application modal state
  const [penaltyModal, setPenaltyModal] = useState(false);
  const [penaltyTermId, setPenaltyTermId] = useState("");
  const [penaltyAmount, setPenaltyAmount] = useState("");
  const [penaltyNote, setPenaltyNote] = useState("");
  const [penaltySubmitting, setPenaltySubmitting] = useState(false);
  const [penaltyError, setPenaltyError] = useState("");

  // Termination confirmation modal state
  const [terminateModal, setTerminateModal] = useState(false);
  const [terminateNote, setTerminateNote] = useState("");

  // Manual close modal state (director/CEO): finished order settled outside the per-POCK
  // flow (e.g. collective invoice). Only from status "accepted_final".
  const [closeModal, setCloseModal] = useState(false);
  const [closeNote, setCloseNote] = useState("Rozliczone fakturą zbiorczą");
  const closeMutation = useCloseOrder();

  // Reopen modal state (director/CEO): the final POCK went out too early — scope grew or
  // acceptance was a mistake — so a FINISHED order goes back to work. See ordersApi.reopen.
  const [reopenModal, setReopenModal] = useState(false);
  const [reopenNote, setReopenNote] = useState("");
  const reopenMutation = useReopenOrder();

  // Annul modal state (director/CEO): the order turned out to be pointless before anyone
  // did anything. Distinct from both "Zerwanie" (contractor at fault, penalty) and
  // "Zamknij" (delivered and settled) — see ordersApi.annul.
  const [annulModal, setAnnulModal] = useState(false);
  const [annulNote, setAnnulNote] = useState("");
  const annulMutation = useAnnulOrder();

  // „Widok podwykonawcy" — modal z podglądem zamówienia oczami przypisanego podwykonawcy.
  const [contractorViewModal, setContractorViewModal] = useState(false);

  // Delete modal state (directors/CEO only, soft delete)
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const deleteMutation = useDeleteOrder();

  // CEO edit modal state
  const isCeo = Boolean(user?.is_ceo);
  // Order edit + attachments: CEO and technical directors. Only the CEO may change the contractor.
  const canEditOrder = isCeo || (isDirector && Boolean(user?.is_technical_director));
  const ceoUpdateMutation = useCEOUpdateOrder();
  // Pole „Pliki pozycji realizacyjnej” w edycji także dla zamawiającego (osoba
  // odpowiedzialna, koordynator przedsięwzięcia, grant „Zarządzanie” — backend:
  // user_manages_order). Zamawiający widzi w modalu tylko to pole; pozycji, do której
  // podwykonawca przesłał już pliki, nie może wyłączyć (checkbox nieaktywny, backend 400).
  const canEditLineFiles = canEditOrder || Boolean(order?.can_manage);
  const lineFilesMutation = useSetLineItemResultFiles();
  // Ta sama zapytanie, co sekcja plików pozycji na stronie — wspólny cache.
  const { data: resultFilesOverview } = useOrderResultFiles(id);
  const linesWithFiles = new Set(
    (resultFilesOverview?.line_items ?? [])
      .filter((li) => (li.files?.length ?? 0) > 0)
      .map((li) => li.id),
  );
  const forceAcceptMutation = useForceAcceptOrder();
  // Restore a contractor-rejected order back to "awaiting confirmation" — technical director and above.
  const canRestoreOrder = isCeo || (isDirector && Boolean(user?.is_technical_director));
  const restoreMutation = useRestoreOrder();
  const [ceoEditModal, setCeoEditModal] = useState(false);
  const [ceoEditForm, setCeoEditForm] = useState({
    description: "",
    deadline: "",
    priority: "standard",
    contractor: "",
    ordered_by: "",
    project: "",
  });
  // Per-line-item "pliki pozycji realizacyjnej" toggles, keyed by line item id.
  const [ceoEditLineFiles, setCeoEditLineFiles] = useState<Record<string, boolean>>({});
  const [ceoEditError, setCeoEditError] = useState("");
  const { data: contractorsList } = useQuery({
    queryKey: ["contractors-list"],
    queryFn: () => apiClient.get<{ data: { id: string; name: string }[] }>("/contractors/").then((r) => r.data.data),
    enabled: isCeo && ceoEditModal,
  });
  // Internal GK users for the Zamawiający (orderer) picker — loaded when the edit modal opens.
  const { data: internalUsersList } = useQuery({
    queryKey: ["internal-users"],
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; full_name: string }[] }>("/auth/internal-users/")
        .then((r) => r.data.data),
    enabled: canEditOrder && ceoEditModal,
  });
  // Przedsięwzięcia for the project picker — the order edit (dyr. tech./CEO) may re-point
  // the order to a different przedsięwzięcie. Loaded when the edit modal opens.
  const { data: projectsList } = useQuery({
    queryKey: ["projects"],
    queryFn: () =>
      apiClient
        .get<{ data: { id: string; name: string }[] }>("/projects/")
        .then((r) => r.data.data),
    enabled: canEditOrder && ceoEditModal,
  });

  const openCeoEditModal = () => {
    if (!order) return;
    setCeoEditForm({
      description: order.description,
      deadline: order.deadline ?? "",
      priority: order.priority,
      contractor: order.contractor,
      ordered_by: order.ordered_by ?? "",
      project: order.project_id ?? "",
    });
    setCeoEditLineFiles(
      Object.fromEntries(order.line_items.map((li) => [li.id, li.result_files_expected])),
    );
    setCeoEditError("");
    setCeoEditModal(true);
  };

  const submitCeoEdit = async () => {
    if (!order) return;
    setCeoEditError("");
    if (!canEditOrder) {
      // Zamawiający: tylko pole plików pozycji, osobna akcja backendu.
      try {
        await lineFilesMutation.mutateAsync({
          id: order.id,
          entries: order.line_items.map((li) => ({
            id: li.id,
            result_files_expected: ceoEditLineFiles[li.id] ?? li.result_files_expected,
          })),
        });
        setCeoEditModal(false);
      } catch (e) {
        const detail = (e as { response?: { data?: { errors?: { detail?: string } } } })?.response
          ?.data?.errors?.detail;
        setCeoEditError(detail || "Błąd podczas zapisywania zmian. Spróbuj ponownie.");
      }
      return;
    }
    try {
      await ceoUpdateMutation.mutateAsync({
        id: order.id,
        payload: {
          description: ceoEditForm.description,
          deadline: ceoEditForm.deadline || null,
          priority: ceoEditForm.priority,
          // Zamawiający (osoba odpowiedzialna GK) — edytowalny przez dyr. tech. oraz CEO.
          ordered_by: ceoEditForm.ordered_by || null,
          // Only the CEO may reassign the contractor.
          ...(isCeo ? { contractor: ceoEditForm.contractor } : {}),
          // Przedsięwzięcie — sent only when actually changed (backend re-points the
          // requisition for requisition-based orders; the order number stays as-is).
          ...(ceoEditForm.project && ceoEditForm.project !== (order.project_id ?? "")
            ? { project: ceoEditForm.project }
            : {}),
          line_item_result_files: order.line_items.map((li) => ({
            id: li.id,
            result_files_expected: ceoEditLineFiles[li.id] ?? li.result_files_expected,
          })),
        },
      });
      setCeoEditModal(false);
    } catch {
      setCeoEditError("Błąd podczas zapisywania zmian. Sprawdź dane i spróbuj ponownie.");
    }
  };

  const openPenaltyModal = (term?: PenaltyCalcTerm) => {
    setPenaltyTermId(term?.term_id ?? "");
    setPenaltyAmount(term?.calculated_amount ?? "");
    setPenaltyNote("");
    setPenaltyError("");
    setPenaltyModal(true);
  };

  const submitPenalty = async () => {
    if (!penaltyCalc?.agreement_id) return;
    setPenaltySubmitting(true);
    setPenaltyError("");
    try {
      await apiClient.post(`/agreements/${penaltyCalc.agreement_id}/penalty-records/`, {
        order: id,
        penalty_term: penaltyTermId || null,
        delay_days: penaltyCalc.days_overdue,
        amount: penaltyAmount,
        note: penaltyNote,
        is_termination: false,
      });
      qc.invalidateQueries({ queryKey: ["orders", id, "penalty-calc"] });
      setPenaltyModal(false);
    } catch {
      setPenaltyError("Błąd podczas zapisywania kary. Sprawdź dane i spróbuj ponownie.");
    } finally {
      setPenaltySubmitting(false);
    }
  };

  const submitTerminate = async () => {
    if (!penaltyCalc?.agreement_id) return;
    await terminateMutation.mutateAsync({
      id: id!,
      payload: {
        amount: penaltyCalc.total_calculated_penalty,
        note: terminateNote,
        agreement_id: penaltyCalc.agreement_id,
        delay_days: penaltyCalc.days_overdue,
      },
    });
    setTerminateModal(false);
  };

  const submitClose = async () => {
    const note = closeNote.trim();
    if (!note) return;
    await closeMutation.mutateAsync({ id: id!, settlement_note: note });
    setCloseModal(false);
  };

  const submitReopen = async () => {
    const note = reopenNote.trim();
    if (!note) return;
    await reopenMutation.mutateAsync({ id: id!, reason: note });
    setReopenModal(false);
  };

  const submitAnnul = async () => {
    const note = annulNote.trim();
    if (!note) return;
    await annulMutation.mutateAsync({ id: id!, reason: note });
    setAnnulModal(false);
  };

  const submitDelete = async () => {
    if (!order) return;
    setDeleteSubmitting(true);
    setDeleteError("");
    try {
      await deleteMutation.mutateAsync(order.id);
      navigate("/orders");
    } catch {
      setDeleteError("Błąd podczas usuwania zamówienia.");
      setDeleteSubmitting(false);
    }
  };

  const { data: protocols } = useQuery({
    queryKey: ["protocols", { order: id }],
    queryFn: () =>
      apiClient.get<{ data: Protocol[] }>("/protocols/", { params: { order: id } })
        .then((r) => r.data.data),
    enabled: !!id,
  });

  const { data: pockRequests } = useQuery({
    queryKey: ["pock-requests", { order: id }],
    queryFn: () =>
      apiClient.get<{ data: PockRequest[] }>("/protocols/pock-requests/", {
        params: { order: id },
      }).then((r) => r.data.data),
    enabled: !!id && canSeePockRequests,
  });

  // --- Modal state ---
  const [pockModal, setPockModal] = useState(false);
  const [step, setStep] = useState<"form" | "attachments">("form");
  const [isFinal, setIsFinal] = useState(false);
  const [pockNotes, setPockNotes] = useState("");
  const [pockRequestId, setPockRequestId] = useState("");
  const [items, setItems] = useState<ItemForm[]>([]);
  const [createdProtocol, setCreatedProtocol] = useState<CreatedProtocol | null>(null);
  const [createError, setCreateError] = useState("");
  const [createPending, setCreatePending] = useState(false);
  // „Utwórz na podstawie istniejącego POCK": the base protocol, what happens to it
  // (in the same transaction as the new one) and whether its files are copied.
  const [sourceId, setSourceId] = useState("");
  const [sourceAction, setSourceAction] = useState<SourceAction>("none");
  const [sourceReason, setSourceReason] = useState("");
  const [copyAttachments, setCopyAttachments] = useState(true);

  const freshItems = (): ItemForm[] =>
    (order?.line_items ?? []).map((li) => ({
      order_line_item: li.id,
      qty: li.quantity,
      quality_pct: "100",
      amount_net: "",
      is_amount_manual: false,
      attachment_required: li.attachment_required,
    }));

  const openPockModal = () => {
    if (!order) return;
    setIsFinal(!order.allow_partial_pock);
    setPockNotes("");
    setPockRequestId("");
    setCreatedProtocol(null);
    setStep("form");
    setCreateError("");
    setSourceId("");
    setSourceAction("none");
    setSourceReason("");
    setCopyAttachments(true);
    setItems(freshItems());
    setPockModal(true);
  };

  /** Pick (or clear) the base protocol: its lines, quantities, quality and typed amounts
   *  prefill the form — still editable — and its request link carries over. */
  const applySource = (sourceProtocolId: string) => {
    if (!order) return;
    setSourceId(sourceProtocolId);
    setSourceAction("none");
    setSourceReason("");
    const source = (protocols ?? []).find((p) => p.id === sourceProtocolId);
    if (!source) {
      setIsFinal(!order.allow_partial_pock);
      setPockRequestId("");
      setItems(freshItems());
      return;
    }
    setIsFinal(!order.allow_partial_pock || source.is_final);
    const linkedRequest = source.pock_request ?? "";
    setPockRequestId((pockRequests ?? []).some((r) => r.id === linkedRequest) ? linkedRequest : "");
    setItems(
      order.line_items.map((li) => {
        const si = source.items.find((it) => it.order_line_item === li.id);
        if (!si) {
          // Not on the base protocol — nothing accepted on this line.
          return {
            order_line_item: li.id, qty: "0", quality_pct: "100", amount_net: "",
            is_amount_manual: false, attachment_required: li.attachment_required,
          };
        }
        const quality = parseFloat(si.quality_pct ?? "100") || 100;
        const qty = qtyInputValue(acceptedQtyFromPct(si.completion_percentage, li.quantity, quality));
        // Same formula as the form's auto amount: only a stored value that disagrees
        // with it was typed by someone and is carried over as a manual amount.
        const auto = ((parseFloat(qty) || 0) * (quality / 100) * (parseFloat(li.unit_rate) || 0)).toFixed(2);
        const manual = !source.without_amounts && isAmountOverridden(si.amount_net, auto);
        return {
          order_line_item: li.id,
          qty,
          quality_pct: String(quality),
          amount_net: manual ? (si.amount_net ?? "") : "",
          is_amount_manual: manual,
          attachment_required: li.attachment_required,
        };
      }),
    );
  };

  const updateItemQty = (index: number, qty: string, orderedQty: string) => {
    const q = parseFloat(qty);
    const Q = parseFloat(orderedQty) || 1;
    const next = [...items];
    // Changing qty clears any manual amount override so auto recalculates
    next[index] = { ...next[index], qty, is_amount_manual: false, amount_net: "" };
    // Clamp qty to [0, orderedQty]
    if (!isNaN(q) && q > Q) {
      next[index].qty = orderedQty;
    }
    setItems(next);
  };

  const updateItemQuality = (index: number, qualityPct: string) => {
    const next = [...items];
    // Changing quality clears manual amount so auto recalculates
    next[index] = { ...next[index], quality_pct: qualityPct, is_amount_manual: false, amount_net: "" };
    setItems(next);
  };

  const updateItemAmount = (index: number, amount: string, isManual: boolean) => {
    const next = [...items];
    next[index] = { ...next[index], amount_net: amount, is_amount_manual: isManual };
    setItems(next);
  };

  // For final protocol: calculate remaining completion per line item from approved partial protocols
  const calcFinalItems = () => {
    if (!order) return items;
    const approvedPartials = (protocols ?? []).filter(
      (p) => !p.is_withdrawn && p.accepted_by_contractor === true && !p.is_final
    );
    const alreadyApproved = new Map<string, number>();
    for (const p of approvedPartials) {
      for (const pi of p.items) {
        alreadyApproved.set(
          pi.order_line_item,
          (alreadyApproved.get(pi.order_line_item) ?? 0) + parseFloat(pi.completion_percentage)
        );
      }
    }
    return order.line_items.map((li) => {
      const used = alreadyApproved.get(li.id) ?? 0;
      const remaining = Math.max(0, 100 - used);
      const remainingQty = qtyInputValue(acceptedQtyFromPct(remaining, li.quantity));
      return {
        order_line_item: li.id,
        qty: remainingQty,
        quality_pct: "100",
        amount_net: "",
        is_amount_manual: false,
        attachment_required: li.attachment_required,
      };
    });
  };

  const submitPock = async () => {
    if (!order) return;
    if (sourceId && sourceAction === "delete" && !sourceReason.trim()) {
      setCreateError("Podaj powód usunięcia starego POCK.");
      return;
    }
    setCreateError("");
    setCreatePending(true);
    const finalItems = isFinal ? calcFinalItems() : items;
    try {
      const payload = {
        order: id,
        is_final: isFinal,
        pock_request: pockRequestId || null,
        notes: pockNotes,
        source_protocol: sourceId || null,
        source_action: sourceId ? sourceAction : "none",
        source_action_reason: sourceReason.trim(),
        copy_attachments: copyAttachments,
        items: finalItems.map((it, idx) => {
          const li = order.line_items[idx];
          // completion_percentage = (accepted_qty / ordered_qty) * quality_pct,
          // at the column's full precision — see pockMath.
          const result: Record<string, unknown> = {
            order_line_item: it.order_line_item,
            completion_percentage: completionPctFromQty(it.qty, li.quantity, it.quality_pct),
          };
          if (it.is_amount_manual && it.amount_net !== "") {
            const parsed = parseFloat(it.amount_net);
            if (!isNaN(parsed)) result.amount_net = parsed;
          }
          return result;
        }),
      };
      const res = await apiClient.post<{ data: CreatedProtocol }>("/protocols/", payload);
      const created = res.data.data;
      qc.invalidateQueries({ queryKey: ["protocols", { order: id }] });
      if (sourceId) qc.invalidateQueries({ queryKey: ["protocols", sourceId] });
      const needsAttach = created.items.some(
        (ci) =>
          !ci.attachment_managed_file &&
          items.find((it) => it.order_line_item === ci.order_line_item)?.attachment_required
      );
      if (needsAttach) {
        setCreatedProtocol(created);
        setStep("attachments");
      } else {
        setPockModal(false);
      }
    } catch (err) {
      setCreateError(apiErrorText(err, "Błąd podczas tworzenia POCK. Sprawdź dane i spróbuj ponownie."));
    } finally {
      setCreatePending(false);
    }
  };

  if (isLoading) return <PageLoader />;
  if (!order) return <p className="text-gray-500 dark:text-gray-400">Nie znaleziono zamówienia.</p>;

  const total = order.line_items.reduce((sum, item) => sum + parseFloat(item.total_value), 0);

  // POCK bez kwot (pracownik realizujący) nie jest rozliczeniem — nie schodzi z wartości zamówienia.
  const settledProtocols = (protocols ?? []).filter((p) => isSettledProtocol(p) && !p.without_amounts);
  const pockSum = settledProtocols.reduce((s, p) => s + protocolNetValue(p).value, 0);
  const pockMaskedCount = settledProtocols.reduce((s, p) => s + protocolNetValue(p).masked, 0);
  const remainingToPay = total - pockSum;
  const maskedStar = pockMaskedCount > 0 ? "*" : "";

  return (
    <div className="max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Badge color={order.priority === "high" ? "red" : "blue"}>
            {PRIORITY_LABELS[order.priority]}
          </Badge>
          <Badge color={statusColors[order.status]}>
            {ORDER_STATUS_LABELS[order.status]}
          </Badge>
        </div>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 break-words">{order.order_number}</h1>
              <OrderTypeBadge type={order.order_type} display={order.order_type_display} />
            </div>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1" />
          </div>
          <div className="flex items-center gap-3 mt-1 shrink-0">
            {/* Powiadomienia o tym zamówieniu — opt-in wyłącznie po stronie GK ELEKTIM.
                Podwykonawca ma własny widok zamówienia i nie zapisuje się na wewnętrzny
                ruch (komentarze GK↔GK, historia zdarzeń); backend odrzuca jego żądanie. */}
            {!isContractorUser && !access.readOnly && (
              <button
                onClick={() => subscriptionMutation.mutate(!order.is_subscribed)}
                disabled={subscriptionMutation.isPending}
                className={`flex items-center gap-1.5 text-xs hover:underline disabled:opacity-50 ${
                  order.is_subscribed
                    ? "text-[var(--color-primary)]"
                    : "text-gray-500 dark:text-gray-400"
                }`}
                title={
                  order.is_subscribed
                    ? "Nie chcę już powiadomień o komentarzach, zdarzeniach i plikach w tym zamówieniu"
                    : "Dostawaj powiadomienia o komentarzach, zdarzeniach i plikach dodanych w tym zamówieniu"
                }
              >
                {order.is_subscribed ? <Bell size={13} /> : <BellOff size={13} />}
                {order.is_subscribed ? "Powiadomienia włączone" : "Włącz powiadomienia"}
              </button>
            )}
            {/* Także dla dostępu „Podgląd": backend maskuje wtedy stawki w podglądzie
                niezależnie od grantów kont podwykonawcy, a modal chowa przełącznik stawek. */}
            <button
              onClick={() => setContractorViewModal(true)}
              className="flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
              title={`Zobacz zamówienie tak, jak widzi je ${order.contractor_name}`}
            >
              <Eye size={13} />
              Widok podwykonawcy
            </button>
            {canEditLineFiles && (
              <button
                onClick={openCeoEditModal}
                className="flex items-center gap-1.5 text-xs text-[var(--color-primary)] hover:underline"
              >
                <Pencil size={13} />
                Edytuj
              </button>
            )}
            {(isDirector || isCeo) && (
              <button
                onClick={() => { setDeleteError(""); setDeleteModal(true); }}
                className="flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 hover:underline"
              >
                <Trash2 size={13} />
                Usuń
              </button>
            )}
          </div>
        </div>
        {/* Termin przyjęcia (tylko „Wysłane”): po nim zamówienie dostaje flagę „długo
            oczekuje” (badge dyrektorów) — nie jest anulowane samo. Zamawiający (backend:
            CanManageOrderObject) może przedłużyć termin, dyrektor — anulować zamówienie. */}
        <AcceptanceDeadlineNotice
          order={order}
          canExtend={Boolean(order.can_manage) && !access.readOnly}
          onCancel={
            isDirector || isCeo
              ? () => {
                  setAnnulNote("Podwykonawca nie przyjął zamówienia w terminie przyjęcia.");
                  setAnnulModal(true);
                }
              : undefined
          }
        />
        {(isDirector || isCeo) && (order.status === "sent" || order.status === "rejected") && (
          <div className="mt-3">
            <Button
              size="sm"
              loading={forceAcceptMutation.isPending}
              onClick={() => {
                if (window.confirm("Wymusić przyjęcie zamówienia bez potwierdzenia podwykonawcy?")) {
                  forceAcceptMutation.mutate(order.id);
                }
              }}
            >
              <CheckCircle2 size={14} /> Wymuś przyjęcie zamówienia
            </Button>
          </div>
        )}
        {canRestoreOrder && order.status === "rejected" && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="secondary"
              loading={restoreMutation.isPending}
              onClick={() => {
                if (window.confirm("Przywrócić odrzucone zamówienie do statusu „Wysłane” (oczekuje na potwierdzenie podwykonawcy)?")) {
                  restoreMutation.mutate(order.id);
                }
              }}
            >
              <RotateCcw size={14} /> Przywróć zamówienie
            </Button>
          </div>
        )}
        {/* Zamówienie przejęte i zakończone przez pracownika (końcowy POCK bez kwot, nic do
            fakturowania): zamyka je też prowadzący — koordynator, zamawiający, „Zarządzanie". */}
        {!(isDirector || isCeo) && access.canManage && !access.readOnly
          && order.status === "accepted_final" && order.finished_without_settlement && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setCloseNote("Zrealizowane przez pracownika GK ELEKTIM — bez rozliczenia z podwykonawcą"); setCloseModal(true); }}
            >
              <CheckCircle2 size={14} /> Zamknij zlecenie
            </Button>
          </div>
        )}
        {(isDirector || isCeo) && order.status === "accepted_final" && (
          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setCloseNote("Rozliczone fakturą zbiorczą"); setCloseModal(true); }}
            >
              <CheckCircle2 size={14} /> Zamknij zlecenie
            </Button>
            {/* Odbiór końcowy poszedł za wcześnie (zakres urósł albo POCK wystawiono
                omyłkowo) — zlecenie wraca do realizacji zamiast zakładania drugiego
                na tę samą robotę. */}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setReopenNote(""); setReopenModal(true); }}
            >
              <RotateCcw size={14} /> Otwórz ponownie
            </Button>
          </div>
        )}
        {/* Zlecenie bezprzedmiotowe — wystawione omyłkowo albo zakres odpadł, zanim
            ktokolwiek cokolwiek zrobił. Bez kary (to „Zerwanie") i bez liczenia jako
            wykonane (to „Zamknij"). Backend odmawia, gdy jest przyjęty POCK lub faktura. */}
        {(isDirector || isCeo) && isActive && (
          <div className="mt-3">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => { setAnnulNote(""); setAnnulModal(true); }}
            >
              <XCircle size={14} /> Anuluj jako bezprzedmiotowe
            </Button>
          </div>
        )}
        {(order.status === "closed" || order.status === "cancelled") && order.closed_reason && (
          <p className="text-gray-500 dark:text-gray-400 text-xs mt-1">
            {order.status === "cancelled" ? "Anulowane" : "Zamknięte"}: {order.closed_reason}
            {order.closed_by_name ? ` — ${order.closed_by_name}` : ""}
          </p>
        )}
        {/* Przedsięwzięcie · podwykonawca. Gdy przedsięwzięcie ma podpięty projekt ZE,
            jego nazwa jest linkiem do workspace'u DLC_01 (trasa kluczuje po id
            przedsięwzięcia, nie po id profilu ZE). */}
        <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
          {order.billing_source === "group_designer_billing" ? (
            order.contractor_name
          ) : (
            <>
              {order.has_ze_project && canOpenZeProject && order.project_id ? (
                <Link
                  to={`/power-projects/${order.project_id}`}
                  title="Przejdź do projektu ZE"
                  className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                >
                  {order.project_name ?? "—"}
                  <ExternalLink size={12} className="shrink-0" />
                </Link>
              ) : (
                (order.project_name ?? "—")
              )}
              {` · ${order.contractor_name}`}
            </>
          )}
        </p>
        {order.billing_source === "group_designer_billing" && order.projects.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 px-2 py-0.5 text-xs font-medium">
              Zamówienie grupowe
            </span>
            <span className="text-gray-500 dark:text-gray-400">
              Dotyczy {order.projects.length} przedsięwzięć:{" "}
              {/* Każde przedsięwzięcie z projektem ZE jest osobnym linkiem — przy
                  zamówieniu grupowym nie ma jednego „tego właściwego". */}
              {order.projects.map((p, i) => (
                <span key={p.id}>
                  {i > 0 && ", "}
                  {p.has_ze_project && canOpenZeProject ? (
                    <Link
                      to={`/power-projects/${p.id}`}
                      title="Przejdź do projektu ZE"
                      className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1"
                    >
                      {p.name}
                      <ExternalLink size={12} className="shrink-0" />
                    </Link>
                  ) : (
                    p.name
                  )}
                </span>
              ))}
            </span>
          </div>
        )}
      </div>

      <ContractorViewModal
        orderId={order.id}
        open={contractorViewModal}
        onClose={() => setContractorViewModal(false)}
      />

      {/* Info cards */}
      <div className="grid grid-cols-2 gap-3 mb-6">
        <InfoCard label="Zatwierdzone przez" value={order.approved_by_name} />
        <InfoCard label="Data zatwierdzenia" value={formatDate(order.approved_at)} />
        <InfoCard label="Termin realizacji" value={formatDate(order.deadline)} />
        <InfoCard label="Wartość zamówienia" value={`${formatAmount(total)} PLN`} />
        <InfoCard label="Rozliczone protokołami POCK" value={`${formatAmount(pockSum)} PLN${maskedStar}`} />
        <InfoCard label="Pozostała kwota do zapłaty" value={`${formatAmount(remainingToPay)} PLN${maskedStar}`} />
      </div>
      {/* Kwota minimalna zamówienia z umowy ramowej — informacja (ostrzeżenie poniżej minimum). */}
      <MinOrderAmountNotice notice={order.min_order_amount_notice} className="mb-6" />

      {/* Dwie strony zamówienia obok siebie: kto zamawia (GK) i kto to wziął (podwykonawca).
          Oba kontakty (telefon/e-mail osób) pochodzą na żywo z profili, więc zmiana
          zamawiającego przez dyr. tech. zmienia je razem z osobą. */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6 items-start">
        {/* Zamawiający — osoba odpowiedzialna po stronie GK ELEKTIM. Pod nim osobna
            sekcja z pracownikami GK, którym nadano dostęp „Zarządzanie”/„Realizacja”
            (może ich być kilku) — obok karty podwykonawcy. */}
        <div className="flex flex-col gap-3">
        <div className="bg-white dark:bg-gray-800 p-4 border-l-4 border-[var(--color-primary)]">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Zamawiający (osoba odpowiedzialna)</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{order.ordered_by_name ?? "—"}</p>
          <div className="mt-2 flex flex-col gap-1 text-sm">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Phone size={13} className="shrink-0" />
              {order.ordered_by_phone ? (
                <a href={`tel:${order.ordered_by_phone}`} className="hover:underline break-all">{order.ordered_by_phone}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Mail size={13} className="shrink-0" />
              {order.ordered_by_email ? (
                <a href={`mailto:${order.ordered_by_email}`} className="hover:underline break-all">{order.ordered_by_email}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
              )}
            </span>
          </div>
        </div>
        <OrderGrantedStaffCard staff={order.granted_staff} />
        </div>

        {/* Wykonawca — firma z kartoteki podwykonawcy oraz osoba, która przyjęła to
            konkretne zlecenie. Osoby brak przy zleceniach przyjętych automatycznie
            (umowa ramowa / oferta wiążąca) do czasu potwierdzenia zapoznania się oraz
            przy przyjęciu wymuszonym przez dyrekcję — piszemy to wprost, żeby nikt nie
            czytał pustego pola jako „brak danych w systemie". */}
        <div className="bg-white dark:bg-gray-800 p-4 border-l-4 border-amber-400">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Podwykonawca (przyjmujący zlecenie)</p>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words flex items-center gap-1.5">
            <Building2 size={13} className="shrink-0 text-gray-400 dark:text-gray-500" />
            {canOpenContractor && order.contractor ? (
              <Link
                to={`/contractors/${order.contractor}`}
                title="Przejdź do kartoteki podwykonawcy"
                className="text-[var(--color-primary)] hover:underline inline-flex items-center gap-1 break-words"
              >
                {order.contractor_name}
                <ExternalLink size={12} className="shrink-0" />
              </Link>
            ) : (
              order.contractor_name
            )}
          </p>
          <div className="mt-2 flex flex-col gap-1 text-sm">
            {order.contractor_contact_person && (
              <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                <UserIcon size={13} className="shrink-0" />
                <span className="break-words">{order.contractor_contact_person}</span>
              </span>
            )}
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Phone size={13} className="shrink-0" />
              {order.contractor_contact_phone ? (
                <a href={`tel:${order.contractor_contact_phone}`} className="hover:underline break-all">{order.contractor_contact_phone}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
              )}
            </span>
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
              <Mail size={13} className="shrink-0" />
              {order.contractor_contact_email ? (
                <a href={`mailto:${order.contractor_contact_email}`} className="hover:underline break-all">{order.contractor_contact_email}</a>
              ) : (
                <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
              )}
            </span>
          </div>

          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">
              Zlecenie przyjął(-ęła)
              {order.accepted_at ? ` — ${new Date(order.accepted_at).toLocaleDateString("pl")}` : ""}
            </p>
            {order.accepted_by_name ? (
              <>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{order.accepted_by_name}</p>
                <div className="mt-2 flex flex-col gap-1 text-sm">
                  <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Phone size={13} className="shrink-0" />
                    {order.accepted_by_phone ? (
                      <a href={`tel:${order.accepted_by_phone}`} className="hover:underline break-all">{order.accepted_by_phone}</a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">brak numeru</span>
                    )}
                  </span>
                  <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-400">
                    <Mail size={13} className="shrink-0" />
                    {order.accepted_by_email ? (
                      <a href={`mailto:${order.accepted_by_email}`} className="hover:underline break-all">{order.accepted_by_email}</a>
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500">brak adresu</span>
                    )}
                  </span>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-400 dark:text-gray-500">
                {order.status === "sent"
                  ? "Czeka na potwierdzenie przez podwykonawcę"
                  : "Brak — zlecenie przyjęte bez potwierdzenia osoby"}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Ocena współpracy po odbiorze — wysoko i w czerwonej obwolucie, bo to jedyny
          moment, w którym ktokolwiek o niej pamięta. Wyłącznie dla GK; endpoint i tak
          odcina podwykonawcę, a sekcja znika, dopóki zamówienie nie jest odebrane. */}
      {user?.role !== "contractor" && <OrderRatingSection orderId={order.id} />}

      {order.description && (
        <div className="bg-gray-50 dark:bg-gray-800/50 p-4 mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Opis</p>
          {order.description.trimStart().startsWith("<") ? (
            <div
              className="rich-text-content text-sm text-gray-600 dark:text-gray-400"
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(order.description, {
                  ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "img", "h1", "h2", "h3"],
                  ALLOWED_ATTR: ["src", "alt", "width", "height", "start"],
                }),
              }}
            />
          ) : (
            <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{order.description}</p>
          )}
        </div>
      )}

      {/* Attachments — CEO/technical directors can add/remove (same permission as the standard order edit). */}
      <OrderAttachmentsSection orderId={order.id} files={order.files_detail ?? []} canManage={canEditOrder} />

      {/* Pliki z projektu ZE udostępnione przy „Zleć podwykonawcy" — żywa projekcja
          zakładki „Pliki"; sekcja sama znika, gdy brak udostępnionych plików. */}
      <OrderProjectFilesSection orderId={order.id} enabled={!!order.has_project_files_share} />

      {/* Kamienie milowe (terminy pośrednie) — read-only dla podwykonawcy, edycja wewnętrzna.
          Zakończone/zamknięte/anulowane zamówienie: lista kamieni zamrożona (bez dodawania,
          usuwania i zmiany kolejności), odhaczanie i terminy nadal edytowalne. */}
      <OrderMilestonesSection
        orderId={order.id}
        milestones={order.milestones ?? []}
        events={order.milestone_events ?? []}
        canManage={user?.role !== "contractor" && !access.readOnly}
        canEditList={!FINISHED_ORDER_STATUSES.includes(order.status)}
      />

      {/* Result files (pliki pozycji realizacyjnej) — self-gated by backend capability flags. */}
      <OrderResultFilesSection orderId={order.id} />

      {/* Komentarze i zdarzenia — osobna sekcja nad powiadomieniami; wątek ma własny,
          ograniczony wysokością scroll, żeby długa korespondencja nie rozpychała strony. */}
      <div ref={commentsRef} className="mb-8 scroll-mt-4">
        <OrderCommentsSection
          orderId={order.id}
          className=""
          threadClassName="max-h-[28rem]"
          description="Komunikacja z zamawiającym w sprawie tego zamówienia."
        />
      </div>

      {/* Czy mail o zamówieniu poszedł do podwykonawcy. Wyłącznie dla GK — endpoint
          zwraca 403 dla konta podwykonawcy (payload zawiera cudze adresy). */}
      <OrderMailLogSection
        orderId={order.id}
        contractorId={order.contractor ?? undefined}
        visible={isDirector || user?.role === "coordinator" || access.canManage}
        finished={["closed", "cancelled", "accepted_final"].includes(order.status)}
      />

      {/* Historia zdarzeń — pełny audyt akcji koordynatora i podwykonawcy na zamówieniu.
          Tylko role wewnętrzne (endpoint zwraca 403 dla konta podwykonawcy). */}
      <OrderEventsSection orderId={order.id} visible={user?.role !== "contractor"} />

      {/* Line items */}
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 border-l-4 border-[var(--color-primary)] pl-3">Pozycje zamówienia</h2>

      {/* Mobile cards */}
      <div className="sm:hidden flex flex-col divide-y divide-gray-100 bg-white dark:bg-gray-800 mb-8">
        {order.line_items.map((item) => (
          <div key={item.id} className={`px-3 py-2.5 border-l-2 ${item.is_custom ? "border-amber-400 bg-amber-50/30 dark:bg-amber-900/10" : "border-transparent"}`}>
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1">
                {item.is_custom && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">[N]</span>}
                {item.line_item_type_name}
              </span>
              <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">{item.total_value != null ? `${formatAmount(item.total_value)} PLN` : ""}</span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {fmtQty(item.quantity)} {item.line_item_type_unit}{item.unit_rate != null ? ` × ${formatAmount(item.unit_rate)} PLN` : ""}
            </p>
          </div>
        ))}
        <div className="px-3 py-2 flex justify-between bg-gray-50 dark:bg-gray-800/50">
          <span className="text-xs font-mono uppercase tracking-widest text-gray-500 dark:text-gray-400">Łącznie</span>
          <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{access.amountsHidden ? "—" : `${formatAmount(total)} PLN`}</span>
        </div>
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto mb-8">
        <table className="w-full text-xs min-w-[440px]">
          <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
            <tr>
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
              <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jm.</th>
              <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość</th>
              <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Stawka</th>
              <th className="text-right px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {order.line_items.map((item) => (
              <tr key={item.id} className={`border-l-2 transition-colors ${item.is_custom ? "border-amber-400 bg-amber-50/30 dark:bg-amber-900/10" : "border-transparent hover:border-[var(--color-primary)]"}`}>
                <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                  <span className="flex items-center gap-1.5">
                    {item.is_custom && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400">[N]</span>}
                    {item.line_item_type_name}
                  </span>
                </td>
                <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">{item.line_item_type_unit}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtQty(item.quantity)}</td>
                <td className="px-3 py-2 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                  {item.unit_rate != null ? `${formatAmount(item.unit_rate)} PLN` : ""}
                </td>
                <td className="px-3 py-2 text-right font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                  {item.total_value != null ? `${formatAmount(item.total_value)} PLN` : ""}
                </td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/50 font-semibold">
              <td colSpan={4} className="px-3 py-2 text-right text-gray-700 dark:text-gray-300">Łącznie:</td>
              <td className="px-3 py-2 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">{access.amountsHidden ? "—" : `${formatAmount(total)} PLN`}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Order change requests — right under the line items they change */}
      <div className="mb-8 bg-white dark:bg-gray-800 p-5">
        <OrderChangePanel orderId={order.id} lineItems={order.line_items} orderStatus={order.status} orderType={order.order_type} />
      </div>

      {/* WPW handover preview (działka | WPW) — read-only; CEO/DLC_01 may confirm entries */}
      <WpwHandoverPanel
        orderId={order.id}
        ownersList={
          <OrderOwnersListSection
            orderId={order.id}
            orderType={order.order_type}
            file={order.mml_file_detail ?? null}
            generatedAt={order.mml_file_generated_at ?? null}
          />
        }
      />

      {/* Design stage (Projekt techniczny / Kosztorys) linked to this order */}
      {(user?.accessible_dlc_modules ?? []).includes("dlc_01") && (
        <DesignStageOrderPanel orderId={order.id} orderType={order.order_type} />
      )}

      {/* MDCP: punkty zdjęciowe przekazane geodecie — tylko dla zamówień MDCP */}
      {/* Wody Polskie — zamrożony wykaz przekroczeń i zbliżeń (zakres zamówienia). */}
      {(user?.accessible_dlc_modules ?? []).includes("dlc_01") && order.order_type === "wody_polskie" && (
        <div className="mt-8"><WodyOrderCrossingsPanel orderId={order.id} /></div>
      )}
      {(user?.accessible_dlc_modules ?? []).includes("dlc_01") && order.order_type === "mdcp_geodezja" && (
        <MdcpPointsPanel orderId={order.id} />
      )}


      {/* Pending POCK requests from contractor */}
      {canSeePockRequests && (pockRequests ?? []).length > 0 && (
        <div className="mt-8 mb-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2 border-l-4 border-[var(--color-primary)] pl-3">
            Wnioski o POCK od podwykonawcy
          </h2>
          <div className="flex flex-col gap-2">
            {(pockRequests ?? []).map((req) => {
              const isExpanded = expandedPockReqs.has(req.id);
              const canDecide = canReviewPockRequests && req.status === "pending" && !req.is_withdrawn;
              const reviewAction = pockReviewId === req.id ? pockReviewAction : null;
              const reviewComment = pockReviewComments[req.id] ?? "";
              const reviewBusy = approvePockRequest.isPending || rejectPockRequest.isPending;
              const toggleExpand = () => setExpandedPockReqs((prev) => {
                const next = new Set(prev);
                next.has(req.id) ? next.delete(req.id) : next.add(req.id);
                return next;
              });
              return (
              <div key={req.id} className="bg-white dark:bg-gray-800 overflow-hidden">
                {/* Row header */}
                <div className="flex items-center justify-between pr-3">
                  <button
                    onClick={toggleExpand}
                    className="flex-1 flex items-center gap-3 flex-wrap px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/70 transition-colors"
                  >
                    <div className={`flex items-center gap-3 flex-wrap flex-1 ${req.is_withdrawn || req.protocol_withdrawn ? "line-through opacity-60" : ""}`}>
                      {req.is_withdrawn ? (
                        <Badge color="gray">Wycofany</Badge>
                      ) : req.protocol_withdrawn ? (
                        <Badge color="gray">POCK wycofany</Badge>
                      ) : (
                        <Badge color={req.status === "pending" ? "yellow" : req.status === "approved" ? "green" : "red"}>
                          {req.status === "pending" ? "Oczekuje" : req.status === "approved" ? "Zatwierdzone" : "Odrzucone"}
                        </Badge>
                      )}
                      {req.proposed_is_final && <Badge color="purple">Końcowy</Badge>}
                      {req.is_stale && req.status === "pending" && !req.is_withdrawn && (
                        <Badge color="red">Nieaktualny — zamówienie zmieniło się po złożeniu</Badge>
                      )}
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {req.requested_by_name} · {new Date(req.created_at).toLocaleDateString("pl")}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {req.approved_settlement_pct != null ? (
                          <>Rozliczenie: <strong className="text-gray-800 dark:text-gray-200">{Math.round(parseFloat(req.approved_settlement_pct))}%</strong></>
                        ) : (
                          <>Proponowane: <strong className="text-gray-800 dark:text-gray-200">{Math.round(parseFloat(req.proposed_settlement_pct))}%</strong></>
                        )}
                      </span>
                      <span className="text-xs font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        {req.total_amount_net != null ? `${formatAmount(req.total_amount_net)} PLN` : "—"}
                      </span>
                      {req.note && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-full whitespace-normal break-words">{req.note}</span>
                      )}
                    </div>
                    {isExpanded ? <ChevronUp size={16} className="text-gray-400 flex-shrink-0" /> : <ChevronDown size={16} className="text-gray-400 flex-shrink-0" />}
                  </button>
                  {/* Shortcuts on the collapsed row; once expanded, the decision block below takes over. */}
                  {canDecide && !isExpanded && (
                    <div className="flex items-center gap-2 pl-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => startPockReview(req, "reject")}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        Odrzuć
                      </Button>
                      <Button
                        size="sm"
                        disabled={req.is_stale}
                        title={req.is_stale ? req.stale_reason : undefined}
                        onClick={() => startPockReview(req, "approve")}
                      >
                        Zatwierdź
                      </Button>
                    </div>
                  )}
                </div>

                {/* Expanded: items + review panel */}
                {isExpanded && (
                  <div className="border-t border-gray-100 dark:border-gray-700">
                    {req.is_stale && req.status === "pending" && !req.is_withdrawn && (
                      <p className="px-4 pt-3 text-sm text-red-600 dark:text-red-400">{req.stale_reason}</p>
                    )}
                    {req.status !== "pending" && req.review_comment && (
                      <p className="px-4 pt-3 text-sm">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Komentarz do decyzji: </span>
                        <span className={req.status === "rejected" ? "text-red-600 dark:text-red-400" : "text-gray-600 dark:text-gray-400"}>
                          {req.review_comment}
                        </span>
                      </p>
                    )}
                    {/* Items table — quantities and money as SUBMITTED (snapshot), not the live line */}
                    <div className="px-4 py-3 overflow-x-auto">
                      <table className="w-full text-xs min-w-[300px]">
                        <thead className="bg-gray-100 dark:bg-gray-700">
                          <tr>
                            <th className="text-left px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                            <th className="text-right px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zamówiono</th>
                            <th className="text-right px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Do odbioru</th>
                            <th className="text-right px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">% rozl.</th>
                            <th className="text-right px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Wartość netto</th>
                            <th className="text-left px-2 py-1.5 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Załączniki</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                          {req.items.map((item) => {
                            const qty = item.accepted_quantity != null
                              ? parseFloat(item.accepted_quantity)
                              : acceptedQtyFromPct(item.completion_percentage, item.order_line_item_quantity);
                            const settlementPct = Math.round(parseFloat(req.approved_settlement_pct ?? req.proposed_settlement_pct));
                            return (
                              <tr key={item.id}>
                                <td className="px-2 py-1.5 text-gray-800 dark:text-gray-200">{item.order_line_item_name}</td>
                                <td className="px-2 py-1.5 text-right text-gray-500">{fmtQty(item.order_line_item_quantity)} {displayUnit(item.order_line_item_unit)}</td>
                                <td className="px-2 py-1.5 text-right font-medium text-gray-900 dark:text-gray-100">{fmtQty(qty)} {displayUnit(item.order_line_item_unit)}</td>
                                <td className="px-2 py-1.5 text-right font-medium text-gray-900 dark:text-gray-100">{settlementPct}%</td>
                                <td className="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  {item.amount_net ? <>{formatAmount(item.amount_net)} PLN</> : "—"}
                                </td>
                                <td className="px-2 py-1.5">
                                  {item.attachments.length === 0 ? (
                                    <span className="text-xs text-gray-400">—</span>
                                  ) : (
                                    <div className="flex flex-col gap-0.5">
                                      {item.attachments.map((att) => (
                                        <a
                                          key={att.id}
                                          href={att.managed_file_detail?.url ?? "#"}
                                          download={att.original_name}
                                          target="_blank"
                                          rel="noreferrer"
                                          className="text-xs text-[var(--color-primary)] hover:underline dark:text-[var(--color-primary)] truncate max-w-[180px] block"
                                          title={att.original_name}
                                        >
                                          {att.original_name}
                                        </a>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="bg-gray-100 dark:bg-gray-700 font-semibold">
                          <tr>
                            <td colSpan={4} className="px-2 py-1.5 text-right text-gray-700 dark:text-gray-300">Łącznie:</td>
                            <td className="px-2 py-1.5 text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                              {req.total_amount_net != null ? `${formatAmount(req.total_amount_net)} PLN` : "—"}
                            </td>
                            <td />
                          </tr>
                        </tfoot>
                      </table>
                    </div>

                    {/* Files of the whole request — copied onto the POCK on approval */}
                    <div className="px-4 pb-3">
                      <DocumentAttachmentsPanel
                        compact
                        title="Pliki całego wniosku"
                        attachments={req.document_attachments ?? []}
                        endpoint={`/protocols/pock-requests/${req.id}/attachments/`}
                        canUpload={canReviewPockRequests && req.status === "pending" && !req.is_withdrawn}
                        invalidateKeys={[["pock-requests"]]}
                      />
                    </div>

                    {/* Decision on the WHOLE request — set apart from its contents above and
                        always there while the request awaits review, so cancelling a half-made
                        decision never takes the comment field away. */}
                    {canDecide && (
                      <div className="mt-2 border-t-2 border-gray-200 dark:border-gray-600 px-4 py-4 bg-gray-50 dark:bg-gray-800/50">
                        <h3 className="font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">
                          Decyzja w sprawie całego wniosku
                        </h3>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 mb-3">
                          Obejmuje wszystkie pozycje i pliki powyżej. Komentarz trafi do podwykonawcy razem z decyzją.
                        </p>
                        <label
                          htmlFor={`pock-review-comment-${req.id}`}
                          className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1"
                        >
                          {reviewAction === "reject" ? "Powód odrzucenia" : "Komentarz dla podwykonawcy"}
                        </label>
                        <textarea
                          id={`pock-review-comment-${req.id}`}
                          value={reviewComment}
                          onChange={(e) => setPockReviewComments((p) => ({ ...p, [req.id]: e.target.value }))}
                          rows={2}
                          className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-3 py-2 text-sm mb-3"
                          placeholder={reviewAction === "reject" ? "Powód odrzucenia..." : "Opcjonalnie..."}
                        />
                        {reviewAction === null ? (
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startPockReview(req, "reject")}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              Odrzuć wniosek…
                            </Button>
                            <Button
                              size="sm"
                              disabled={req.is_stale}
                              title={req.is_stale ? req.stale_reason : undefined}
                              onClick={() => startPockReview(req, "approve")}
                            >
                              Zatwierdź wniosek…
                            </Button>
                          </div>
                        ) : (
                          <div className={`border-l-4 pl-3 py-1 ${reviewAction === "reject" ? "border-red-500" : "border-green-600"}`}>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">
                              {reviewAction === "approve"
                                ? `Zatwierdzasz cały wniosek — powstanie z niego POCK${pockApprovedFinal ? " końcowy" : ""}.`
                                : "Odrzucasz cały wniosek."}
                            </p>
                            {reviewAction === "approve" && (
                              <div className="flex flex-wrap items-center gap-2 mb-3">
                                <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                                  % rozliczenia pozycji ujętych w POCK:
                                </label>
                                <input
                                  type="number"
                                  min={0}
                                  max={100}
                                  step={1}
                                  value={pockApprovedPct}
                                  onChange={(e) => setPockApprovedPct(e.target.value)}
                                  className="w-20 text-right rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 px-2 py-1 text-sm"
                                />
                                <span className="text-sm text-gray-500">%</span>
                                <span className="text-xs text-gray-400">(proponowane: {Math.round(parseFloat(req.proposed_settlement_pct))}%)</span>
                                <label className="flex items-center gap-2 ml-4 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={pockApprovedFinal}
                                    onChange={(e) => setPockApprovedFinal(e.target.checked)}
                                    className="rounded"
                                  />
                                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">POCK końcowy</span>
                                  <span className="text-xs text-gray-400">
                                    (wniosek: {req.proposed_is_final ? "końcowy" : "częściowy"})
                                  </span>
                                </label>
                              </div>
                            )}
                            {reviewAction === "approve" && pockApprovedFinal && (
                              <p className="text-xs text-amber-600 dark:text-amber-400 -mt-1 mb-3">
                                POCK zostanie od razu zaakceptowany, a zamówienie oznaczone jako Zakończone.
                              </p>
                            )}
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant={reviewAction === "reject" ? "ghost" : "primary"}
                                className={reviewAction === "reject" ? "text-red-600 hover:text-red-700 hover:bg-red-50" : undefined}
                                onClick={async () => {
                                  if (reviewAction === "approve") {
                                    await approvePockRequest.mutateAsync({
                                      id: req.id,
                                      comment: reviewComment,
                                      approved_settlement_pct: pockApprovedPct,
                                      is_final: pockApprovedFinal,
                                    });
                                  } else {
                                    await rejectPockRequest.mutateAsync({ id: req.id, comment: reviewComment });
                                  }
                                  qc.invalidateQueries({ queryKey: ["pock-requests"] });
                                  qc.invalidateQueries({ queryKey: ["protocols", { order: id }] });
                                  setPockReviewComments((p) => {
                                    const next = { ...p };
                                    delete next[req.id];
                                    return next;
                                  });
                                  cancelPockReview();
                                }}
                                disabled={reviewBusy}
                              >
                                {reviewBusy ? "Zapisywanie…" : reviewAction === "approve" ? "Zatwierdź wniosek" : "Odrzuć wniosek"}
                              </Button>
                              <Button size="sm" variant="ghost" onClick={cancelPockReview}>
                                Anuluj
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        </div>
      )}

      {/* POCK section */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-l-4 border-[var(--color-primary)] pl-3">Protokoły POCK</h2>
        {canManagePock && (
          <Button size="sm" onClick={openPockModal}>
            <Plus size={14} /> Nowy POCK
          </Button>
        )}
      </div>

      {(protocols ?? []).length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">Brak protokołów dla tego zamówienia.</p>
      ) : (
        <div className="flex flex-col gap-2">
          {[...(protocols ?? [])].sort((a, b) => {
            if (a.is_withdrawn === b.is_withdrawn) return 0;
            return a.is_withdrawn ? 1 : -1;
          }).map((p) => {
            const { value, masked } = protocolNetValue(p);
            return (
            <Link
              key={p.id}
              to={`/protocols/${p.id}`}
              className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 bg-white dark:bg-gray-800 px-4 py-3 hover:border-[var(--color-primary)] hover:shadow-sm transition-all"
            >
              <div className="flex items-center gap-3">
                <FileText size={16} className="text-gray-400 dark:text-gray-500 shrink-0" />
                <span className={`font-medium ${p.is_withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-900 dark:text-gray-100"}`}>
                  {p.protocol_number}
                </span>
                {p.is_withdrawn ? (
                  <Badge color="gray">Wycofany</Badge>
                ) : (
                  <Badge color={p.is_final ? "purple" : "blue"}>
                    {p.is_final ? "Końcowy" : "Częściowy"}
                  </Badge>
                )}
                {p.without_amounts && <Badge color="orange" title="POCK bez kwot — wystawiony przez pracownika realizującego jako przekazanie prac; bez faktury i UZ/UoD">Bez kwot</Badge>}
                {p.source_protocol_number && (
                  <span className="text-xs text-gray-400 dark:text-gray-500">korekta {p.source_protocol_number}</span>
                )}
              </div>
              <div className="flex items-center gap-3 flex-wrap">
                {!p.is_withdrawn && (
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                    {masked === p.items.length && p.items.length > 0
                      ? "—"
                      : `${formatAmount(value)} PLN${masked > 0 ? "*" : ""}`}
                  </span>
                )}
                {!p.is_withdrawn && (
                  <Badge
                    color={
                      p.accepted_by_contractor === null ? "gray"
                      : p.accepted_by_contractor ? "green" : "red"
                    }
                  >
                    {p.accepted_by_contractor === null
                      ? "Oczekuje"
                      : p.accepted_by_contractor
                      ? "Zaakceptowany"
                      : "Odrzucony"}
                  </Badge>
                )}
                <span className="text-xs text-gray-400 dark:text-gray-500">
                  {new Date(p.created_at).toLocaleDateString("pl")}
                </span>
              </div>
            </Link>
            );
          })}
          {/* Settlement summary: initial order value minus POCK protocols */}
          {!access.amountsHidden && (
          <div className="bg-gray-50 dark:bg-gray-800/50 px-4 py-3 flex flex-col gap-1.5 border-l-4 border-[var(--color-primary)]">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Wartość początkowa zamówienia</span>
              <span className="font-medium text-gray-900 dark:text-gray-100">{formatAmount(total)} PLN</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">Suma protokołów POCK (bez wycofanych i odrzuconych)</span>
              <span className="font-medium text-gray-500 dark:text-gray-400">− {formatAmount(pockSum)} PLN{maskedStar}</span>
            </div>
            <div className="flex justify-between text-sm border-t border-gray-200 dark:border-gray-700 pt-1.5 mt-0.5">
              <span className="font-semibold text-[var(--color-primary)]">Pozostała kwota do zapłaty</span>
              <span className={`font-bold ${remainingToPay < 0 ? "text-red-600 dark:text-red-400" : "text-[var(--color-primary)]"}`}>
                {formatAmount(remainingToPay)} PLN{maskedStar}
              </span>
            </div>
            {pockMaskedCount > 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500">
                * część kwot jest ukryta (brak dostępu do stawek) — suma obejmuje tylko widoczne pozycje
              </p>
            )}
          </div>
          )}
        </div>
      )}

      {/* Create POCK modal */}
      <Modal
        open={pockModal}
        onClose={() => setPockModal(false)}
        title={step === "form" ? "Nowy protokół POCK" : "Załączniki do pozycji"}
        size="lg"
      >
        {step === "form" ? (
          <PockFormStep
            order={order}
            protocols={protocols ?? []}
            items={items}
            isFinal={isFinal}
            notes={pockNotes}
            pockRequestId={pockRequestId}
            pockRequests={pockRequests ?? []}
            setIsFinal={setIsFinal}
            setNotes={setPockNotes}
            setPockRequestId={setPockRequestId}
            updateItemQty={updateItemQty}
            updateItemQuality={updateItemQuality}
            updateItemAmount={updateItemAmount}
            onSubmit={submitPock}
            onCancel={() => setPockModal(false)}
            isPending={createPending}
            error={createError}
            source={{
              protocolId: sourceId,
              action: sourceAction,
              reason: sourceReason,
              copyAttachments,
              allowedActions: allowedSourceActions(
                (protocols ?? []).find((p) => p.id === sourceId),
                user,
              ),
              onSelect: applySource,
              setAction: setSourceAction,
              setReason: setSourceReason,
              setCopyAttachments,
            }}
          />
        ) : (
          <AttachmentStep
            createdProtocol={createdProtocol!}
            items={items}
            onDone={() => setPockModal(false)}
          />
        )}
      </Modal>

      {/* Director: penalty/termination panel */}
      {isDirector && penaltyCalc && penaltyCalc.days_overdue > 0 && (
        <div className="mt-8 rounded-xl border border-red-300 bg-red-50 p-5">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-700 text-base">
                Zamówienie opóźnione o {penaltyCalc.days_overdue}{" "}
                {penaltyCalc.days_overdue === 1 ? "dzień" : "dni"}
              </h3>
              <p className="text-sm text-red-600">
                Termin realizacji: <span className="font-medium">{formatDate(penaltyCalc.deadline)}</span>
                {penaltyCalc.agreement_title && (
                  <> · Umowa: <span className="font-medium">{penaltyCalc.agreement_title}</span></>
                )}
              </p>
            </div>
          </div>

          {penaltyCalc.terms.length > 0 ? (
            <>
              <div className="mb-4 overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-red-200">
                      <th className="text-left py-2 text-red-800 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kara</th>
                      <th className="text-right py-2 text-red-800 pr-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Podstawa</th>
                      <th className="text-right py-2 text-red-800 pr-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Możliwa kara</th>
                      <th className="text-right py-2 text-red-800 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Akcja</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {penaltyCalc.terms.map((term) => (
                      <tr key={term.term_id}>
                        <td className="py-2 text-red-900">{term.description}</td>
                        <td className="py-2 text-right pr-2 text-red-700">
                          {formatAmount(term.base_value)} PLN
                        </td>
                        <td className="py-2 text-right pr-2 font-semibold text-red-700">
                          {formatAmount(term.calculated_amount)} PLN
                        </td>
                        <td className="py-2 text-right">
                          {penaltyCalc.agreement_id && (
                            <button
                              onClick={() => openPenaltyModal(term)}
                              className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                            >
                              Nalicz karę
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {penaltyCalc.terms.length > 1 && (
                    <tfoot>
                      <tr className="border-t border-red-300">
                        <td colSpan={2} className="pt-2 font-bold text-red-800">Łącznie możliwa kara</td>
                        <td className="pt-2 text-right font-bold text-red-800">
                          {formatAmount(penaltyCalc.total_calculated_penalty)} PLN
                        </td>
                        <td className="pt-2 text-right">
                          {penaltyCalc.agreement_id && (
                            <button
                              onClick={() => openPenaltyModal()}
                              className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
                            >
                              Nalicz łącznie
                            </button>
                          )}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
              {penaltyCalc.termination_possible && (
                <div className="pt-3 border-t border-red-300">
                  <p className="text-sm font-semibold text-red-800 mb-2">
                    Warunki umowy dopuszczają zerwanie zlecenia z winy podwykonawcy.
                  </p>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => { setTerminateNote(""); setTerminateModal(true); }}
                  >
                    Zerwij zlecenie z winy podwykonawcy
                  </Button>
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-red-600">
              {penaltyCalc.agreement_id
                ? "Brak zdefiniowanych kar w umowie ramowej."
                : "Brak powiązanej umowy ramowej — nie można naliczyć kar."}
            </p>
          )}
        </div>
      )}

      {/* Penalty application modal */}
      <Modal open={penaltyModal} onClose={() => setPenaltyModal(false)} title="Nalicz karę umowną">
        <div className="flex flex-col gap-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Kwota kary (PLN)
            </label>
            <input
              type="number"
              step="0.01"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={penaltyAmount}
              onChange={(e) => setPenaltyAmount(e.target.value)}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uzasadnienie
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={penaltyNote}
              onChange={(e) => setPenaltyNote(e.target.value)}
              placeholder="Opis podstawy naliczenia kary..."
            />
          </div>
          {penaltyError && <p className="text-sm text-red-600">{penaltyError}</p>}
          <div className="flex gap-3">
            <Button loading={penaltySubmitting} onClick={submitPenalty}>Zapisz karę</Button>
            <Button variant="secondary" onClick={() => setPenaltyModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Terminate order confirmation modal */}
      <Modal open={terminateModal} onClose={() => setTerminateModal(false)} title="Zerwanie zlecenia z winy podwykonawcy">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Czy na pewno chcesz zerwać to zlecenie? Zamówienie zostanie anulowane, a kara umowna
            w kwocie <span className="font-semibold text-red-700">
              {formatAmount(penaltyCalc?.total_calculated_penalty ?? "0")} PLN
            </span> zostanie naliczona automatycznie.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uzasadnienie</label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              value={terminateNote}
              onChange={(e) => setTerminateNote(e.target.value)}
              placeholder="Opis przyczyny zerwania zlecenia..."
            />
          </div>
          {terminateMutation.isError && (
            <p className="text-sm text-red-600">Błąd podczas zerwania zlecenia. Spróbuj ponownie.</p>
          )}
          <div className="flex gap-3">
            <Button
              variant="danger"
              loading={terminateMutation.isPending}
              onClick={submitTerminate}
            >
              Potwierdź zerwanie
            </Button>
            <Button variant="secondary" onClick={() => setTerminateModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Manual close modal (director/CEO) — order settled outside the per-POCK flow */}
      <Modal open={closeModal} onClose={() => setCloseModal(false)} title="Zamknij zlecenie">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Zamknięcie oznacza zlecenie jako „Zamknięte” — użyj, gdy zostało rozliczone poza
            standardowym obiegiem POCK (np. fakturą zbiorczą). Podaj uzasadnienie.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uzasadnienie zamknięcia</label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={closeNote}
              onChange={(e) => setCloseNote(e.target.value)}
              placeholder="np. Rozliczone fakturą zbiorczą"
            />
          </div>
          {closeMutation.isError && (
            <p className="text-sm text-red-600">Błąd podczas zamykania zlecenia. Spróbuj ponownie.</p>
          )}
          <div className="flex gap-3">
            <Button
              loading={closeMutation.isPending}
              disabled={!closeNote.trim()}
              onClick={submitClose}
            >
              Zamknij zlecenie
            </Button>
            <Button variant="secondary" onClick={() => setCloseModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Reopen modal (director/CEO) — finished order that is not finished after all */}
      <Modal open={reopenModal} onClose={() => setReopenModal(false)} title="Otwórz ponownie zlecenie">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Zlecenie wróci do realizacji — użyj, gdy odbiór końcowy poszedł za wcześnie
            (zakres urósł o kolejne pakiety albo POCK wystawiono omyłkowo). Wystawione
            protokoły zostają nietknięte; błędny POCK wycofaj osobno. Podwykonawca dostanie
            powiadomienie, że robota wróciła na jego biurko.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Uzasadnienie ponownego otwarcia
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={reopenNote}
              onChange={(e) => setReopenNote(e.target.value)}
              placeholder="np. Zakres rośnie o kolejne pakiety WPW — odbiór końcowy przedwczesny"
            />
          </div>
          {reopenMutation.isError && (
            <p className="text-sm text-red-600">
              {(reopenMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                ?.response?.data?.errors?.detail ?? "Błąd podczas otwierania zlecenia. Spróbuj ponownie."}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              loading={reopenMutation.isPending}
              disabled={!reopenNote.trim()}
              onClick={submitReopen}
            >
              Otwórz ponownie
            </Button>
            <Button variant="secondary" onClick={() => setReopenModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Annul modal (director/CEO) — order that turned out to be pointless */}
      <Modal open={annulModal} onClose={() => setAnnulModal(false)} title="Anuluj zlecenie jako bezprzedmiotowe">
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Użyj, gdy zlecenie okazało się niepotrzebne — wystawione omyłkowo albo jego zakres
            odpadł, zanim ktokolwiek cokolwiek zrobił. Zlecenie trafi do „Anulowane”:
            <strong> bez kary umownej</strong> dla podwykonawcy i <strong>bez liczenia jako
            wykonane</strong>, więc nie wejdzie do ocen podwykonawcy ani nie zapali kamienia
            w przedsięwzięciu. Podaj uzasadnienie.
          </p>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uzasadnienie anulowania</label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={annulNote}
              onChange={(e) => setAnnulNote(e.target.value)}
              placeholder="np. Sprawa nie leży w gestii Nadzoru Wodnego — zlecenie bezprzedmiotowe"
            />
          </div>
          {annulMutation.isError && (
            <p className="text-sm text-red-600">
              {(annulMutation.error as { response?: { data?: { errors?: { detail?: string } } } })
                ?.response?.data?.errors?.detail
                ?? "Błąd podczas anulowania zlecenia. Spróbuj ponownie."}
            </p>
          )}
          <div className="flex gap-3">
            <Button
              loading={annulMutation.isPending}
              disabled={!annulNote.trim()}
              onClick={submitAnnul}
            >
              Anuluj zlecenie
            </Button>
            <Button variant="secondary" onClick={() => setAnnulModal(false)}>Wróć</Button>
          </div>
        </div>
      </Modal>

      {/* CEO edit modal */}
      <Modal open={ceoEditModal} onClose={() => setCeoEditModal(false)} title="Edytuj zamówienie">
        <div className="flex flex-col gap-4">
          {/* Parametry zamówienia — dyr. tech./CEO. Zamawiający dostaje tu tylko pole plików pozycji. */}
          {canEditOrder && (
            <>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Opis</label>
            <RichTextEditor
              value={ceoEditForm.description}
              onChange={(html) => setCeoEditForm((f) => ({ ...f, description: html }))}
              placeholder="Opis zamówienia..."
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Termin realizacji</label>
            <DatePicker
              value={ceoEditForm.deadline}
              onChange={(v) => setCeoEditForm((f) => ({ ...f, deadline: v }))}
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Priorytet</label>
            <select
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
              value={ceoEditForm.priority}
              onChange={(e) => setCeoEditForm((f) => ({ ...f, priority: e.target.value }))}
            >
              <option value="low">Niski</option>
              <option value="standard">Standardowy</option>
              <option value="high">Wysoki</option>
            </select>
          </div>
          {/* Przedsięwzięcie — dyr. tech./CEO. Zmiana NIE przenumerowuje zamówienia (numer
              zachowuje skrót przedsięwzięcia, pod którym powstał). */}
          <SearchableSelect
            label={order.projects.length > 1 ? "Przedsięwzięcie (główne)" : "Przedsięwzięcie"}
            placeholder="— wybierz —"
            value={ceoEditForm.project}
            onChange={(e) => setCeoEditForm((f) => ({ ...f, project: e.target.value }))}
            disabled={!projectsList}
            options={
              projectsList
                ? projectsList.map((p) => ({ value: p.id, label: p.name }))
                : [{ value: ceoEditForm.project, label: order.project_name ?? "" }]
            }
          />
          {/* Zamawiający (osoba odpowiedzialna GK) — dyr. tech./CEO. Zmiana osoby zmienia też
              prezentowany telefon i e-mail (pochodzą z jej profilu). */}
          <SearchableSelect
            label="Zamawiający (osoba odpowiedzialna)"
            placeholder="— wybierz —"
            value={ceoEditForm.ordered_by}
            onChange={(e) => setCeoEditForm((f) => ({ ...f, ordered_by: e.target.value }))}
            disabled={!internalUsersList}
            options={
              internalUsersList
                ? internalUsersList.map((u) => ({ value: u.id, label: u.full_name }))
                : [{ value: ceoEditForm.ordered_by, label: order.ordered_by_name ?? "" }]
            }
          />
          {/* Only the CEO may reassign the contractor; technical directors edit the other fields. */}
          {isCeo && (
            <SearchableSelect
              label="Podwykonawca"
              placeholder="— wybierz —"
              value={ceoEditForm.contractor}
              onChange={(e) => setCeoEditForm((f) => ({ ...f, contractor: e.target.value }))}
              disabled={!contractorsList}
              options={
                contractorsList
                  ? contractorsList.map((c) => ({ value: c.id, label: c.name }))
                  : [{ value: ceoEditForm.contractor, label: order.contractor_name ?? "" }]
              }
            />
          )}
            </>
          )}
          {order.line_items.length > 0 && (
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">
                Pliki pozycji realizacyjnej
              </label>
              <div className="flex flex-col gap-2 rounded-md border border-gray-200 dark:border-gray-700 p-3">
                {order.line_items.map((li) => {
                  // Zamawiający nie wyłączy pozycji, do której podwykonawca przesłał już pliki
                  // (zniknęłyby z widoku). Dyr. tech./CEO edytują jak dotąd.
                  const locked =
                    !canEditOrder && li.result_files_expected && linesWithFiles.has(li.id);
                  return (
                    <label
                      key={li.id}
                      className={`flex items-start gap-2 ${locked ? "cursor-not-allowed" : "cursor-pointer"}`}
                      title={locked ? "Podwykonawca przesłał już pliki do tej pozycji — nie można wyłączyć." : undefined}
                    >
                      <input
                        type="checkbox"
                        checked={ceoEditLineFiles[li.id] ?? li.result_files_expected}
                        disabled={locked}
                        onChange={(e) =>
                          setCeoEditLineFiles((m) => ({ ...m, [li.id]: e.target.checked }))
                        }
                        className="rounded mt-0.5 disabled:opacity-50"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        {li.line_item_type_name}
                        <span className="block text-xs text-gray-500 dark:text-gray-400">
                          {li.quantity} {li.line_item_type_unit} —{" "}
                          {locked
                            ? "podwykonawca przesłał już pliki, nie można wyłączyć"
                            : "podwykonawca przesyła pliki wynikowe"}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}
          {ceoEditError && <p className="text-sm text-red-600">{ceoEditError}</p>}
          <div className="flex gap-3">
            <Button loading={ceoUpdateMutation.isPending || lineFilesMutation.isPending} onClick={submitCeoEdit}>
              Zapisz zmiany
            </Button>
            <Button variant="secondary" onClick={() => setCeoEditModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Delete order modal (director/CEO) */}
      <Modal open={deleteModal} onClose={() => setDeleteModal(false)} title="Usuń zamówienie">
        <div className="flex flex-col gap-4">
          <div className="flex items-start gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded">
            <AlertTriangle size={20} className="text-red-600 dark:text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800 dark:text-red-300">
                Zamówienie zostanie trwale ukryte z systemu.
              </p>
              <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                {order?.order_number} · {order?.contractor_name}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Czy na pewno chcesz usunąć to zamówienie? Operacja jest odwracalna tylko przez administratora bazy danych.
          </p>
          {deleteError && <p className="text-sm text-red-600">{deleteError}</p>}
          <div className="flex gap-3">
            <Button variant="danger" loading={deleteSubmitting} onClick={submitDelete}>
              <Trash2 size={14} /> Usuń zamówienie
            </Button>
            <Button variant="secondary" onClick={() => setDeleteModal(false)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* „Daj dostęp pracownikowi GK Elektim” — zamawiający i dyrektorzy techniczni. */}
      <OrderAccessGrantsSection orderId={order.id} canGrant={!!order.can_grant_access} />

      {/* Contractor fee requests toggle (director only) */}
      {isDirector && (
        <div className="mt-6 bg-white dark:bg-gray-800 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2 mb-3">
            <Settings2 size={16} />
            Uprawnienia podwykonawcy
          </h2>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={order.contractor_fee_requests_allowed}
              onChange={async (e) => {
                await apiClient.patch(`/orders/${order.id}/patch-flags/`, {
                  contractor_fee_requests_allowed: e.target.checked,
                });
                qc.invalidateQueries({ queryKey: ["orders", order.id] });
              }}
              className="rounded"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Zezwalaj na opłaty urzędowe (podwykonawca może wnioskować o opłatę urzędową)
            </span>
          </label>
        </div>
      )}

      {/* Weekly reports (director/coordinator view) */}
      {canManagePock && (
        <div className="mt-8">
          {/* Report settings panel */}
          <div className="mb-4 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Settings2 size={16} />
                Cotygodniowe raporty postępu
              </h2>
              {!showReportSettingsEdit && !access.readOnly && (
                <button
                  onClick={() => {
                    setReportSettingsForm({
                      requires_weekly_reports: order.requires_weekly_reports,
                      report_day_of_week: order.report_day_of_week != null ? String(order.report_day_of_week) : "",
                      report_due_time: order.report_due_time ?? "",
                    });
                    setShowReportSettingsEdit(true);
                  }}
                  className="flex items-center gap-1 text-xs text-[var(--color-primary)] dark:text-[var(--color-primary)] hover:underline"
                >
                  <Pencil size={12} /> Nadpisz ustawienia
                </button>
              )}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mb-2">
              {order.effective_requires_weekly_reports
                ? <>Wymagane · Standardowy
                    {order.effective_report_day_of_week != null && <> · {["Pn","Wt","Śr","Cz","Pt","So","Nd"][order.effective_report_day_of_week]}{order.effective_report_due_time ? ` godz. ${order.effective_report_due_time}` : ""}</>}
                    {(order.requires_weekly_reports != null || order.report_day_of_week != null || order.report_due_time) && <span className="ml-1 text-orange-500">(nadpisane na zleceniu)</span>}
                  </>
                : <span>Wyłączone — ustaw w umowie ramowej lub nadpisz tutaj</span>
              }
            </div>
            {showReportSettingsEdit && (
              <div className="border-t border-gray-100 dark:border-gray-700 pt-3 mt-2 flex flex-col gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">
                    Wymagaj raportów
                  </label>
                  <select
                    className="block rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                    value={reportSettingsForm.requires_weekly_reports === null ? "" : String(reportSettingsForm.requires_weekly_reports)}
                    onChange={(e) => setReportSettingsForm((p) => ({
                      ...p,
                      requires_weekly_reports: e.target.value === "" ? null : e.target.value === "true",
                    }))}
                  >
                    <option value="">Domyślnie z umowy ramowej</option>
                    <option value="true">Tak — wymagaj</option>
                    <option value="false">Nie — wyłącz</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Dzień składania</label>
                    <select
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                      value={reportSettingsForm.report_day_of_week}
                      onChange={(e) => setReportSettingsForm((p) => ({ ...p, report_day_of_week: e.target.value }))}
                    >
                      <option value="">Domyślnie z umowy</option>
                      {["Poniedziałek","Wtorek","Środa","Czwartek","Piątek","Sobota","Niedziela"].map((d, i) => (
                        <option key={i} value={String(i)}>{d}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 dark:text-gray-400 block mb-1">Godzina deadline</label>
                    <input
                      type="time"
                      className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-1.5 text-sm"
                      value={reportSettingsForm.report_due_time}
                      onChange={(e) => setReportSettingsForm((p) => ({ ...p, report_due_time: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    loading={updateReportSettings.isPending}
                    onClick={() => {
                      updateReportSettings.mutate({
                        requires_weekly_reports: reportSettingsForm.requires_weekly_reports,
                        report_day_of_week: reportSettingsForm.report_day_of_week !== "" ? parseInt(reportSettingsForm.report_day_of_week) : null,
                        report_due_time: reportSettingsForm.report_due_time || null,
                      }, { onSuccess: () => setShowReportSettingsEdit(false) });
                    }}
                  >
                    Zapisz
                  </Button>
                  <Button size="sm" variant="secondary" onClick={() => setShowReportSettingsEdit(false)}>Anuluj</Button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Weekly reports list */}
      {order.effective_requires_weekly_reports && (
        <div className="mt-0">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2 border-l-4 border-[var(--color-primary)] pl-3">
            <FileText size={18} />
            Lista raportów
            {weeklyReports.filter((r) => r.status === "submitted" && !r.director_read).length > 0 && (
              <span className="ml-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold">
                {weeklyReports.filter((r) => r.status === "submitted" && !r.director_read).length}
              </span>
            )}
          </h2>
          {weeklyReports.length === 0 ? (
            <p className="text-sm text-gray-400 dark:text-gray-500">Brak raportów dla tego zamówienia.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {weeklyReports.map((report) => {
                const statusColor: Record<WeeklyReportStatus, "yellow" | "green" | "red" | "blue" | "gray"> = {
                  pending: "yellow", submitted: "blue", late: "red", accepted: "green", rejected: "red",
                };
                const isUnread = report.status === "submitted" && !report.director_read;
                return (
                  <div
                    key={report.id}
                    role="button"
                    tabIndex={0}
                    className={`bg-white dark:bg-gray-800 rounded-xl border p-4 cursor-pointer hover:border-[var(--color-primary)] transition-colors ${isUnread ? "border-blue-300 dark:border-[var(--color-primary)]" : "border-gray-200 dark:border-gray-700"}`}
                    onClick={() => {
                      if (isUnread) markWeeklyReportRead.mutate(report.id);
                      navigate(`/orders/${id}/weekly-reports/${report.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        if (isUnread) markWeeklyReportRead.mutate(report.id);
                        navigate(`/orders/${id}/weekly-reports/${report.id}`);
                      }
                    }}
                  >
                    <div className="flex items-start justify-between gap-2 flex-wrap">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge color={statusColor[report.status]}>{WEEKLY_REPORT_STATUS_LABELS[report.status]}</Badge>
                        {isUnread && <Badge color="blue">Nowy</Badge>}
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Termin: {new Date(report.due_date).toLocaleDateString("pl")}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Okres: {new Date(report.period_start).toLocaleDateString("pl")} – {new Date(report.period_end).toLocaleDateString("pl")}
                        </span>
                        {report.submitted_at && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            Złożono: {new Date(report.submitted_at).toLocaleString("pl")}
                          </span>
                        )}
                      </div>
                      {report.status === "submitted" && !access.readOnly && (
                        <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            onClick={() => acceptWeeklyReport.mutate(report.id)}
                            disabled={acceptWeeklyReport.isPending}
                          >
                            <CheckCircle2 size={13} /> Zatwierdź
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setWeeklyRejectId(report.id); setWeeklyRejectComment(""); }}
                          >
                            <XCircle size={13} /> Odrzuć
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Reject comment input */}
                    {weeklyRejectId === report.id && (
                      <div className="mt-3 border-t border-gray-100 dark:border-gray-700 pt-3" onClick={(e) => e.stopPropagation()}>
                        <textarea
                          className="w-full rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm px-3 py-2 text-gray-900 dark:text-gray-100 mb-2"
                          rows={2}
                          placeholder="Komentarz do odrzucenia (wymagany)"
                          value={weeklyRejectComment}
                          onChange={(e) => setWeeklyRejectComment(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            disabled={!weeklyRejectComment.trim() || rejectWeeklyReport.isPending}
                            onClick={() => rejectWeeklyReport.mutate(
                              { id: report.id, comment: weeklyRejectComment },
                              { onSuccess: () => setWeeklyRejectId(null) }
                            )}
                          >
                            Potwierdź odrzucenie
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setWeeklyRejectId(null)}>Anuluj</Button>
                        </div>
                      </div>
                    )}

                    {/* Director comment on accepted/rejected */}
                    {report.director_comment && (
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">
                        Komentarz: {report.director_comment}
                      </p>
                    )}

                    {/* Files */}
                    {report.files.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {report.files.map((f) => (
                          <a
                            key={f.id}
                            href={f.file_url ?? f.file}
                            download={f.original_name}
                            target="_blank"
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            className="text-xs text-[var(--color-primary)] hover:underline dark:text-[var(--color-primary)] truncate max-w-[200px]"
                            title={f.original_name}
                          >
                            {f.original_name}
                          </a>
                        ))}
                      </div>
                    )}


                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* External costs */}
      <ExternalCostsSection orderId={order.id} />
    </div>
  );
}

// --- „Utwórz na podstawie istniejącego POCK" ---

interface PockSourceState {
  protocolId: string;
  action: SourceAction;
  reason: string;
  copyAttachments: boolean;
  allowedActions: SourceAction[];
  onSelect: (protocolId: string) => void;
  setAction: (a: SourceAction) => void;
  setReason: (v: string) => void;
  setCopyAttachments: (v: boolean) => void;
}

const SOURCE_ACTION_LABELS: Record<SourceAction, string> = {
  none: "Nic — zostaw bez zmian",
  reject: "Odrzuć — zastąpiony nowym protokołem",
  delete: "Usuń z systemu",
  accept: "Zatwierdź (zaakceptowany obustronnie)",
};

function protocolStatusLabel(p: Protocol): string {
  if (p.is_withdrawn) return "wycofany";
  if (p.accepted_by_contractor === true) return "zaakceptowany";
  if (p.accepted_by_contractor === false) return "odrzucony";
  return "oczekuje";
}

/** Files a copy carries over: the lines' current attachments + the files of the whole protocol. */
function sourceFileCount(p: Protocol): number {
  const lineFiles = p.items.reduce((n, it) => {
    const current = (it.attachment_versions ?? []).filter((v) => !v.is_superseded).length;
    return n + (current || (it.attachment_managed_file ? 1 : 0));
  }, 0);
  return lineFiles + (p.document_attachments ?? []).length;
}

/** What may happen to the base protocol — the backend's rules
 *  (apps/deliveries/pock_actions.py), so the form offers nothing it would refuse. */
function allowedSourceActions(
  p: Protocol | undefined,
  user: { id: string; role: string; is_ceo?: boolean } | null | undefined,
): SourceAction[] {
  const actions: SourceAction[] = ["none"];
  if (!p || p.is_withdrawn) return actions;
  if (p.accepted_by_contractor === false) {
    if (p.can_delete) actions.push("delete");
    return actions;
  }
  if (p.accepted_by_contractor === null) {
    actions.push("reject");
    const coordinatorMayAccept =
      user?.role === "coordinator" && p.created_by === user.id && protocolNetValue(p).value < 5000;
    if (!p.awaiting_orderer_acceptance && (user?.is_ceo || coordinatorMayAccept)) actions.push("accept");
  }
  return actions;
}

function onlyNoneHint(p: Protocol): string {
  if (p.is_withdrawn) return "Wycofany POCK zostaje bez zmian.";
  if (p.accepted_by_contractor === true) return "Zaakceptowany POCK zostaje bez zmian.";
  if (p.accepted_by_contractor === false) {
    return p.delete_block_reason || "Usunąć odrzucony POCK może dyrektor techniczny albo zarząd (CEO).";
  }
  return "";
}

function PockSourceSection({
  protocols,
  protocolId,
  action,
  reason,
  copyAttachments,
  allowedActions,
  onSelect,
  setAction,
  setReason,
  setCopyAttachments,
}: PockSourceState & { protocols: Protocol[] }) {
  const source = protocols.find((p) => p.id === protocolId);
  const files = source ? sourceFileCount(source) : 0;
  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex flex-col gap-3" data-testid="pock-source-section">
      <div>
        <label htmlFor="pock-source" className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
          Utwórz na podstawie istniejącego POCK (opcjonalnie)
        </label>
        <select
          id="pock-source"
          className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
          value={protocolId}
          onChange={(e) => onSelect(e.target.value)}
        >
          <option value="">— nowy protokół od zera —</option>
          {protocols.map((p) => {
            const { value, masked } = protocolNetValue(p);
            const valueLabel = p.without_amounts
              ? "bez kwot"
              : masked === p.items.length && p.items.length > 0
                ? "—"
                : `${formatAmount(value)} PLN${masked > 0 ? "*" : ""}`;
            return (
              <option key={p.id} value={p.id}>
                {p.protocol_number} · {new Date(p.created_at).toLocaleDateString("pl")} · {protocolStatusLabel(p)} · {valueLabel}
              </option>
            );
          })}
        </select>
      </div>
      {source && (
        <>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Pozycje, ilości, jakość i ręczne kwoty przepisano z {source.protocol_number} — możesz je poprawić
            poniżej. Nowy POCK zapamięta, z którego protokołu powstał.
          </p>
          <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              className="rounded"
              checked={copyAttachments}
              onChange={(e) => setCopyAttachments(e.target.checked)}
            />
            Skopiuj pliki z {source.protocol_number} ({files === 0 ? "brak plików" : `plików: ${files}`})
          </label>
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Co zrobić z {source.protocol_number} ({protocolStatusLabel(source)})?
            </legend>
            <div className="flex flex-col gap-1">
              {allowedActions.map((a) => (
                <label key={a} className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="radio"
                    name="pock-source-action"
                    value={a}
                    checked={action === a}
                    onChange={() => setAction(a)}
                  />
                  {SOURCE_ACTION_LABELS[a]}
                </label>
              ))}
            </div>
            {allowedActions.length === 1 && onlyNoneHint(source) && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{onlyNoneHint(source)}</p>
            )}
          </fieldset>
          {(action === "delete" || action === "reject") && (
            <textarea
              aria-label={action === "delete" ? "Powód usunięcia starego POCK" : "Komentarz do odrzucenia starego POCK"}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                action === "delete"
                  ? "Powód usunięcia (wymagany) — trafi do historii zamówienia"
                  : "Komentarz do odrzucenia (opcjonalnie)"
              }
            />
          )}
        </>
      )}
    </div>
  );
}

// --- POCK Form Step ---

interface PockFormStepProps {
  order: ReturnType<typeof useOrder>["data"] & {};
  protocols: Protocol[];
  items: ItemForm[];
  isFinal: boolean;
  notes: string;
  pockRequestId: string;
  pockRequests: PockRequest[];
  setIsFinal: (v: boolean) => void;
  setNotes: (v: string) => void;
  setPockRequestId: (v: string) => void;
  updateItemQty: (i: number, qty: string, ordered: string) => void;
  updateItemQuality: (i: number, qualityPct: string) => void;
  updateItemAmount: (i: number, amount: string, isManual: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
  isPending: boolean;
  /** Server's refusal text ("" = no error). */
  error: string;
  source?: PockSourceState;
}

export function PockFormStep({
  order,
  protocols,
  items,
  isFinal,
  notes,
  pockRequestId,
  pockRequests,
  setIsFinal,
  setNotes,
  setPockRequestId,
  updateItemQty,
  updateItemQuality,
  updateItemAmount,
  onSubmit,
  onCancel,
  isPending,
  error,
  source,
}: PockFormStepProps) {
  if (!order) return null;

  // For final protocol: calculate base value and already-approved partial amounts
  const approvedPartials = protocols.filter(
    (p) => !p.is_withdrawn && p.accepted_by_contractor === true && !p.is_final
  );
  const alreadyApprovedByItem = new Map<string, number>();
  for (const p of approvedPartials) {
    for (const pi of p.items) {
      alreadyApprovedByItem.set(
        pi.order_line_item,
        (alreadyApprovedByItem.get(pi.order_line_item) ?? 0) + parseFloat(pi.completion_percentage)
      );
    }
  }
  const baseValue = order.line_items.reduce(
    (sum: number, li: OrderLineItem) => sum + parseFloat(li.quantity) * parseFloat(li.unit_rate),
    0
  );
  const approvedValue = order.line_items.reduce((sum: number, li: OrderLineItem) => {
    const usedPct = alreadyApprovedByItem.get(li.id) ?? 0;
    return sum + (usedPct / 100) * parseFloat(li.quantity) * parseFloat(li.unit_rate);
  }, 0);
  const finalValue = Math.max(0, baseValue - approvedValue);

  return (
    <div className="flex flex-col gap-5">
      {source && protocols.length > 0 && <PockSourceSection protocols={protocols} {...source} />}

      {pockRequests.length > 0 && (
        <div>
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
            Powiąż z prośbą podwykonawcy (opcjonalnie)
          </label>
          <select
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            value={pockRequestId}
            onChange={(e) => setPockRequestId(e.target.value)}
          >
            <option value="">— brak —</option>
            {pockRequests.map((r) => (
              <option key={r.id} value={r.id}>
                Prośba z {new Date(r.created_at).toLocaleDateString("pl")}
                {r.note ? ` — ${r.note.slice(0, 60)}` : ""}
              </option>
            ))}
          </select>
        </div>
      )}

      <label className={`flex items-center gap-2 ${order.allow_partial_pock ? "cursor-pointer" : "cursor-not-allowed"}`}>
        <input
          type="checkbox"
          checked={isFinal}
          onChange={(e) => setIsFinal(e.target.checked)}
          disabled={!order.allow_partial_pock}
          className="rounded disabled:opacity-60"
          data-testid="is-final-checkbox"
        />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Odbiór końcowy</span>
        {!order.allow_partial_pock && (
          <span className="text-xs text-amber-600 dark:text-amber-400">(wymagane – umowa nie zezwala na protokoły częściowe)</span>
        )}
      </label>

      {isFinal ? (
        // Final protocol: show auto-calculated summary instead of per-item breakdown
        <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 rounded-lg p-4 flex flex-col gap-2">
          <p className="text-sm font-semibold text-[var(--color-primary)] mb-1">Wartość protokołu końcowego (auto)</p>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Wartość bazowa zamówienia</span>
            <span className="font-medium">{formatAmount(baseValue)} PLN</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Już zatwierdzone (częściowe)</span>
            <span className="font-medium text-gray-500 dark:text-gray-400">− {formatAmount(approvedValue)} PLN</span>
          </div>
          <div className="flex justify-between text-sm border-t border-blue-200 pt-2 mt-1">
            <span className="font-semibold text-[var(--color-primary)]">Wartość końcowego POCK</span>
            <span className="font-bold text-[var(--color-primary)]">{formatAmount(finalValue)} PLN</span>
          </div>
        </div>
      ) : (
        <div>
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Pozycje protokołu</p>
          <div className="rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="text-left px-3 py-2 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Pozycja</th>
                  <th className="text-right px-3 py-2 w-20 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Zam.</th>
                  <th className="text-right px-3 py-2 w-28 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Ilość odbioru</th>
                  <th className="text-right px-3 py-2 w-24 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Jakość %</th>
                  <th className="text-right px-3 py-2 w-40 font-mono text-xs uppercase tracking-widest text-gray-500 dark:text-gray-400">Kwota netto (PLN)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {order.line_items.map((li: OrderLineItem, idx: number) => {
                  const Q = parseFloat(li.quantity) || 1;
                  const q = parseFloat(items[idx]?.qty ?? li.quantity) || 0;
                  const qp = parseFloat(items[idx]?.quality_pct ?? "100") || 100;
                  const r = parseFloat(li.unit_rate) || 0;
                  const autoAmount = (q * (qp / 100) * r).toFixed(2);
                  const isManual = items[idx]?.is_amount_manual ?? false;
                  const displayAmount = isManual ? (items[idx]?.amount_net ?? autoAmount) : autoAmount;

                  return (
                    <tr key={li.id}>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        {li.is_custom && <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 mr-1">[N]</span>}
                        {li.line_item_type_name}
                        <span className="text-gray-400 dark:text-gray-500 ml-1 text-xs">{displayUnit(li.line_item_type_unit)}</span>
                        {li.attachment_required && (
                          <span className="ml-1 text-xs text-amber-600 font-medium">(zał. wymagany)</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right text-gray-600 dark:text-gray-400">{fmtQty(li.quantity)}</td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          max={li.quantity}
                          step="0.01"
                          value={items[idx]?.qty ?? fmtQty(li.quantity)}
                          onChange={(e) => updateItemQty(idx, e.target.value, li.quantity)}
                          className="w-24 text-right rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="1"
                          value={items[idx]?.quality_pct ?? "100"}
                          onChange={(e) => updateItemQuality(idx, e.target.value)}
                          className="w-20 text-right rounded border border-gray-300 px-2 py-1 text-sm"
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex flex-col items-end gap-0.5">
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={displayAmount}
                              onChange={(e) => {
                                const v = e.target.value;
                                const parsed = parseFloat(v);
                                const isNowManual = !isNaN(parsed) && v !== autoAmount;
                                updateItemAmount(idx, v, isNowManual);
                              }}
                              className={`w-28 text-right rounded border px-2 py-1 text-sm ${
                                isManual
                                  ? "border-red-400 bg-red-50 text-red-700 font-semibold"
                                  : "border-gray-300"
                              }`}
                            />
                            {isManual && (
                              <button
                                type="button"
                                onClick={() => updateItemAmount(idx, autoAmount, false)}
                                className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700"
                                title="Przywróć automatyczną wartość"
                              >
                                ↺
                              </button>
                            )}
                          </div>
                          {isManual && (
                            <span className="text-xs text-red-500">Domyślnie wyliczona: {autoAmount}</span>
                          )}
                          {!isManual && q < Q && qp === 100 && (
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {((q / Q) * 100).toFixed(1)}% ilości
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">Uwagi (opcjonalnie)</label>
        <textarea
          className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Dodatkowe uwagi do protokołu..."
        />
      </div>

      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <div className="flex gap-3 pt-1">
        <Button loading={isPending} onClick={onSubmit}>Wystaw POCK</Button>
        <Button variant="secondary" onClick={onCancel}>Anuluj</Button>
      </div>
    </div>
  );
}

// --- Attachment Item Row (each has its own file input ref) ---

function AttachmentItemRow({
  ci,
  protocolId,
}: {
  ci: CreatedProtocol["items"][number];
  protocolId: string;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploaded, setUploaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(false);
    const fd = new FormData();
    fd.append("attachment", file);
    try {
      await apiClient.patch(
        `/protocols/${protocolId}/items/${ci.id}/attachment/`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setUploaded(true);
    } catch {
      setError(true);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 px-4 py-3">
      <span className="text-sm text-gray-800 dark:text-gray-200">
        {ci.order_line_item_name}
        <span className="text-gray-400 dark:text-gray-500 ml-1 text-xs">{displayUnit(ci.order_line_item_unit)}</span>
      </span>
      <div className="flex items-center gap-2">
        {error && <span className="text-xs text-red-500">Błąd wgrywania</span>}
        {uploaded ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-700 font-medium">
            <Paperclip size={12} /> Dodano
          </span>
        ) : (
          <>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1 text-xs text-[var(--color-primary)] border border-[var(--color-primary)] rounded px-2 py-1 hover:bg-[var(--color-primary-dim)] disabled:opacity-50"
            >
              <Upload size={12} />
              {uploading ? "Wgrywanie..." : "Dodaj załącznik"}
            </button>
            <input ref={fileRef} type="file" className="hidden" onChange={handleFileChange} />
          </>
        )}
      </div>
    </div>
  );
}

// --- Attachment Step ---

interface AttachmentStepProps {
  createdProtocol: CreatedProtocol;
  items: ItemForm[];
  onDone: () => void;
}

function AttachmentStep({ createdProtocol, items, onDone }: AttachmentStepProps) {
  const requiredItems = createdProtocol.items.filter(
    (ci) =>
      !ci.attachment_managed_file &&
      items.find((it) => it.order_line_item === ci.order_line_item)?.attachment_required
  );

  return (
    <div className="flex flex-col gap-5">
      <p className="text-sm text-gray-600 dark:text-gray-400">
        POCK został wystawiony. Poniższe pozycje wymagają załącznika — dodaj je teraz lub wróć później
        z poziomu szczegółów protokołu.
      </p>

      <div className="flex flex-col gap-2">
        {requiredItems.map((ci) => (
          <AttachmentItemRow key={ci.id} ci={ci} protocolId={createdProtocol.id} />
        ))}
      </div>

      <div className="flex gap-3 pt-1">
        <Button onClick={onDone}>Zakończ</Button>
      </div>
    </div>
  );
}

function InfoCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 p-3">
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 break-words">{value}</p>
    </div>
  );
}
